import type { ApolloClient } from "@apollo/client";
import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GET_EMAIL_INCOMINGS } from "graphql/query/communication-gql";
import CommunicationConst from "../communication-const";
import { getCommunicationDayKey } from "./communication-date-helper";
import type {
  TCommunicationAiResponseStatus,
  TCommunicationAuditEntry,
  TCommunicationContextGroup,
  TCommunicationDismissReason,
  TCommunicationEmailHeaderMeta,
  TCommunicationEquipmentChange,
  TCommunicationFilters,
  TCommunicationFinalChanges,
  TCommunicationQuestion,
  TCommunicationQueueTotals,
  TCommunicationRequestGroup,
  TCommunicationRequestItem,
  TCommunicationSortState,
  TCommunicationStandUpdates,
  TCommunicationSummary,
  TCommunicationTab,
  TCommunicationTask,
  TCommunicationTaskCapabilities,
  TCommunicationTaskFormValues,
  TCommunicationTaskWorkState,
} from "./communication-types-helper";

type TCommunicationWhere = {
  column?: string;
  operator?: string;
  value?: unknown;
  AND?: TCommunicationWhere[];
  OR?: TCommunicationWhere[];
};

type TPaginatorData<TItem> = {
  paginatorInfo?: {
    lastPage?: number | null;
    total?: number | null;
  };
  data?: TItem[];
};

type TCommunicationOrderByClause = {
  column: string;
  order: "ASC" | "DESC";
};

type TCommunicationStoreOptions = {
  page: number;
  pageSize: number;
};

type TCommunicationFullTaskCache = {
  key: string;
  tasks: TCommunicationTask[];
  queueTotals: TCommunicationQueueTotals;
};

const FULL_TASK_CACHE_LIMIT = 8;

type TRawEmailRequest = {
  id?: string | null;
  email_incoming_id?: string | null;
  ee_id?: string | null;
  stand_id?: string | null;
  equipment_id?: string | null;
  flooring_id?: string | null;
  exhibition_name?: string | null;
  company_name?: string | null;
  stand_number?: string | null;
  action?: "ADD" | "REMOVE" | "CHANGE" | "QUESTION" | null;
  item_name?: string | null;
  item_qty?: number | null;
  item_notes?: string | null;
  confidence?: number | null;
  status?: "PENDING" | "UNIDENTIFIED" | "APPROVED" | "REJECTED" | "CANCELLED" | null;
  ai_data?: unknown;
  processed_by_id?: string | null;
  processed_at?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  relExhibitionEvent?: {
    id?: string | null;
    title?: string | null;
    organizer_id?: string | null;
    exhibition_type_id?: string | null;
    organizer?: {
      id?: string | null;
      title?: string | null;
    } | null;
    exhibitionType?: {
      id?: string | null;
      title?: string | null;
    } | null;
  } | null;
  relStand?: {
    id?: string | null;
    stand_number?: string | null;
    company_name?: string | null;
    relCad?: {
      id?: string | null;
      file_path?: string | null;
      getFilePathPreview?: string | null;
    } | null;
  } | null;
  relEquipment?: {
    id?: string | null;
    group_id?: string | null;
    title?: string | null;
    color?: string | null;
  } | null;
  relFlooring?: {
    id?: string | null;
    title?: string | null;
    color?: string | null;
  } | null;
};

type TRawEmailAiResponse = {
  id?: string | null;
  email_incoming_id?: string | null;
  provider?: string | null;
  model?: string | null;
  data_request?: unknown;
  intent?: string | null;
  confidence?: number | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  total_tokens?: number | null;
  response_time_ms?: number | null;
  status?: "processing" | "success" | "retry" | "error" | null;
  needs_review?: boolean | null;
  attempt?: number | null;
  parsed_response?: unknown;
  data_response?: unknown;
  error_message?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type TRawEmailIncoming = {
  id?: string | null;
  email_channel_id?: string | null;
  message_id?: string | null;
  message_uid?: number | null;
  subject?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  body_html?: string | null;
  header_meta?: {
    cc?: string | string[] | null;
    to?: string | string[] | null;
    from?: string | null;
    sent_at?: string | null;
    subject?: string | null;
    priority?: string | null;
  } | null;
  body_clean?: string | null;
  intent?: string | null;
  status?: string | null;
  parse_error?: string | null;
  parsed_data?: unknown;
  used_token?: number | null;
  received_at?: string | null;
  parsed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  getAiResponseCount?: {
    total?: number | null;
    processing?: number | null;
  } | null;
  relEmailChannel?: {
    id?: string | null;
    title?: string | null;
  } | null;
  relEmailAiResponses?: TRawEmailAiResponse[] | null;
  relEmailRequests?: TRawEmailRequest[] | null;
};

const INCOMING_PAGE_SIZE = 50;
const REQUEST_FILTER_FIELDS: Array<keyof Pick<
  TCommunicationFilters,
  "event_name" | "company_name" | "stand_number"
>> = [ "event_name", "company_name", "stand_number" ];

const AI_SUMMARY_KEYS = [ "ai_summary", "summary", "short_summary", "overview" ];
const AI_REASON_KEYS = [ "ai_reason", "reason", "explanation", "match_reason" ];
// TODO: When the backend exposes stable selector-ready event/company/stand entities,
// prefer ids + labels from the API and keep these text fallbacks only as a bridge.
const AI_EVENT_KEYS = [ "event_name", "exhibition_name", "event", "exhibition", "ee_name", "ee_title", "event_title", "exhibition_title" ];
const AI_COMPANY_KEYS = [ "company_name", "company", "client_name", "client", "customer_name", "customer" ];
const AI_STAND_KEYS = [ "stand_number", "stand", "stand_no", "stand_label", "booth_number", "booth" ];
const AI_ACTION_KEYS = [ "action", "change_action", "change_type", "operation", "request_type" ];
const AI_ITEM_KEYS = [
  "name",
  "title",
  "item_name",
  "item",
  "equipment_name",
  "equipment_title",
  "equipment",
  "product_name",
  "product",
  "label",
  "requested_item",
  "requested_equipment",
];
const AI_QTY_KEYS = [ "item_qty", "qty", "quantity", "count", "amount", "pieces", "pcs" ];
const AI_CONFIDENCE_KEYS = [ "item_confidence", "confidence", "score", "match_confidence", "match_score" ];
const AI_NOTES_KEYS = [ "item_notes", "notes", "note", "comment", "details", "description" ];
const AI_QUESTION_KEYS = [
  "question",
  "question_text",
  "clarification",
  "clarification_text",
  "customer_question",
  "customer_message",
  "message",
  "text",
];

type TNormalizedAiFallback = {
  requestItems: TCommunicationRequestItem[];
  requestGroups: TCommunicationRequestGroup[];
  questions: TCommunicationQuestion[];
  finalChanges: TCommunicationFinalChanges;
  eventName: string;
  eventId?: string;
  companyName: string;
  standNumber: string;
  standNumbers: string[];
  standId?: string;
  confidence: number;
  parserProcessedAt?: string;
  sourceIntent?: string;
  aiQuestionText?: string;
  aiSelectedResponseId?: string;
  aiProvider?: string;
  aiModel?: string;
  aiAttempt?: number;
  aiResponseStatus?: TCommunicationAiResponseStatus | null;
  aiNeedsReview?: boolean | null;
  aiResponseConfidence?: number | null;
  aiResponseTimeMs?: number | null;
  aiResponseCountTotal?: number | null;
  aiResponseCountProcessing?: number | null;
  aiHasParsedResponse?: boolean;
  aiHasDataResponse?: boolean;
};

type TSelectedAiPayload = {
  response: TRawEmailAiResponse;
  parsedPayload: unknown;
  dataPayload: unknown;
};

type TDerivedAiMetadata = {
  aiSelectedResponseId?: string;
  aiProvider?: string;
  aiModel?: string;
  aiAttempt?: number;
  aiResponseStatus?: TCommunicationAiResponseStatus | null;
  aiNeedsReview?: boolean | null;
  aiResponseConfidence?: number | null;
  aiResponseTimeMs?: number | null;
  aiResponseCountTotal?: number | null;
  aiResponseCountProcessing?: number | null;
  aiHasParsedResponse: boolean;
  aiHasDataResponse: boolean;
  aiQuestionText?: string;
};

const createFinalChanges = (
  equipmentChanges: TCommunicationEquipmentChange[],
  standUpdates: TCommunicationStandUpdates,
  managerNote: string,
  replyHandoffNote = ""
): TCommunicationFinalChanges => ({
  equipment_changes: equipmentChanges,
  stand_updates: standUpdates,
  manager_note: managerNote,
  reply_handoff_note: replyHandoffNote,
});

const mapFormValuesToFinalChanges = (
  values: TCommunicationTaskFormValues
): TCommunicationFinalChanges =>
  createFinalChanges(
    values.equipment_changes || [],
    values.stand_updates || {},
    values.manager_note || "",
    values.reply_handoff_note || ""
  );

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getScalarString = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}`;
  }

  return "";
};

const pickFirstNonEmpty = (...values: unknown[]): string =>
  values
    .map((value) => getString(value))
    .find(Boolean) || "";

const pickFirstScalarString = (...values: unknown[]): string =>
  values
    .map((value) => getScalarString(value))
    .find(Boolean) || "";

const normalizeHeaderMetaValue = (
  value: string | string[] | null | undefined
): string => {
  if (Array.isArray(value)) {
    return uniqueValues(value.map((item) => getString(item))).join(", ");
  }

  return getString(value);
};

const normalizeHeaderMetaList = (
  value: string | string[] | null | undefined
): string[] => {
  if (Array.isArray(value)) {
    return uniqueValues(value.map((item) => getString(item)));
  }

  const normalizedValue = getString(value);

  return normalizedValue ? [ normalizedValue ] : [];
};

const extractEmailAddress = (value: string): string => {
  const bracketMatch = value.match(/<([^>]+)>/u);

  if (bracketMatch?.[1]) {
    return bracketMatch[1].trim();
  }

  const rawMatch = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/iu);

  return rawMatch?.[0]?.trim() || "";
};

const extractSenderName = (value: string): string => {
  const normalizedValue = getString(value).trim();

  if (!normalizedValue) {
    return "";
  }

  const withoutEmail = normalizedValue.replace(/<[^>]+>/gu, "").replace(/^"+|"+$/gu, "").trim();

  if (!withoutEmail || withoutEmail === normalizedValue && normalizedValue.includes("@")) {
    return "";
  }

  return withoutEmail;
};

const normalizeHeaderMeta = (
  headerMeta?: TRawEmailIncoming["header_meta"]
): TCommunicationEmailHeaderMeta | undefined => {
  if (!headerMeta) {
    return undefined;
  }

  const normalizedMeta: TCommunicationEmailHeaderMeta = {
    cc: normalizeHeaderMetaList(headerMeta.cc),
    to: normalizeHeaderMetaValue(headerMeta.to) || undefined,
    from: getString(headerMeta.from) || undefined,
    sent_at: getString(headerMeta.sent_at) || undefined,
    subject: getString(headerMeta.subject) || undefined,
    priority: getString(headerMeta.priority) || undefined,
  };

  if (
    !normalizedMeta.cc?.length
    && !normalizedMeta.to
    && !normalizedMeta.from
    && !normalizedMeta.sent_at
    && !normalizedMeta.subject
    && !normalizedMeta.priority
  ) {
    return undefined;
  }

  return normalizedMeta;
};

const normalizeConfidence = (value: number | null | undefined): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  return value > 0 && value <= 1 ? Math.round(value * 100) : Math.round(value);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeMixedJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue || !/^[[{]/u.test(normalizedValue)) {
    return value;
  }

  try {
    return JSON.parse(normalizedValue);
  } catch {
    return value;
  }
};

const resolveAiResponsePayload = (response: TRawEmailAiResponse): unknown => {
  const parsedPayload = normalizeMixedJsonValue(response.parsed_response);

  if (parsedPayload !== null && parsedPayload !== undefined && !(typeof parsedPayload === "string" && !parsedPayload.trim())) {
    return parsedPayload;
  }

  return normalizeMixedJsonValue(response.data_response);
};

const getAiParsedPayload = (response: TRawEmailAiResponse): unknown =>
  normalizeMixedJsonValue(response.parsed_response);

const getAiDataPayload = (response: TRawEmailAiResponse): unknown =>
  normalizeMixedJsonValue(response.data_response);

const hasUsableAiPayload = (value: unknown): boolean =>
  value !== null
  && value !== undefined
  && !(typeof value === "string" && !value.trim());

const getAiRootRecord = (value: unknown): Record<string, unknown> | undefined => {
  const normalizedValue = normalizeMixedJsonValue(value);

  return isRecord(normalizedValue) ? normalizedValue : undefined;
};

const getAiStandScopedRecords = (
  value: unknown,
  nestedKey: "items" | "questions"
): Record<string, unknown>[] => {
  const normalizedValue = normalizeMixedJsonValue(value);

  if (!isRecord(normalizedValue) || !Array.isArray(normalizedValue.stands)) {
    return [];
  }

  return normalizedValue.stands.flatMap((stand) => {
    const nestedRecords = isRecord(stand) ? stand[ nestedKey ] : undefined;

    if (!isRecord(stand) || !Array.isArray(nestedRecords)) {
      return [];
    }

    const standContext = Object.fromEntries(
      Object.entries(stand).filter(([ key ]) => key !== "items" && key !== "questions")
    );

    return nestedRecords
      .filter((item): item is Record<string, unknown> => isRecord(item))
      .map((item) => ({
        ...standContext,
        ...item,
      }));
  });
};

const getAiItemRecords = (value: unknown): Record<string, unknown>[] => {
  const normalizedValue = normalizeMixedJsonValue(value);
  const standScopedItems = getAiStandScopedRecords(normalizedValue, "items");

  if (standScopedItems.length) {
    return standScopedItems;
  }

  if (Array.isArray(normalizedValue)) {
    return normalizedValue.filter((item): item is Record<string, unknown> => isRecord(item));
  }

  if (isRecord(normalizedValue) && Array.isArray(normalizedValue.items)) {
    return normalizedValue.items.filter((item): item is Record<string, unknown> => isRecord(item));
  }

  return [];
};

const getAiQuestionRecords = (value: unknown): Record<string, unknown>[] => {
  const normalizedValue = normalizeMixedJsonValue(value);
  const standScopedQuestions = getAiStandScopedRecords(normalizedValue, "questions");

  if (Array.isArray(normalizedValue)) {
    return standScopedQuestions;
  }

  const rootQuestions =
    isRecord(normalizedValue) && Array.isArray(normalizedValue.questions)
      ? normalizedValue.questions.filter((item): item is Record<string, unknown> => isRecord(item))
      : [];

  return [ ...rootQuestions, ...standScopedQuestions ];
};

const getDirectString = (record: Record<string, unknown>, keys: string[]): string =>
  keys
    .map((key) => record[ key ])
    .map((value) => {
      if (typeof value === "string") {
        return value.trim();
      }

      if (typeof value === "number" && Number.isFinite(value)) {
        return `${value}`;
      }

      return "";
    })
    .find(Boolean) || "";

const getDirectNumber = (record: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = record[ key ];

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string") {
      const normalizedValue = value.replace(",", ".").trim();

      if (/^-?\d+(?:\.\d+)?$/u.test(normalizedValue)) {
        return Number(normalizedValue);
      }
    }
  }

  return undefined;
};

const extractStandNumberFromText = (value: unknown): string => {
  const normalizedValue = getString(value);

  if (!normalizedValue) {
    return "";
  }

  const directMatch = normalizedValue.match(/\b[A-Z]{1,4}-\d{1,4}\b/iu);

  if (directMatch?.[0]) {
    return directMatch[0].toUpperCase();
  }

  const compactMatch = normalizedValue.match(/\b(?:OG|B|A|F|M)-\d{1,4}\b/iu);

  if (compactMatch?.[0]) {
    return compactMatch[0].toUpperCase();
  }

  return "";
};

const extractStandNumber = (value: unknown): string => {
  const normalizedStandNumber = extractStandNumberFromText(value);

  if (normalizedStandNumber) {
    return normalizedStandNumber;
  }

  return getString(value);
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");

const normalizeEventNameCandidate = (value: string): string =>
  value
    .replace(/^(?:RE|FW|WG)\s*:\s*/iu, "")
    .replace(/[|:/\\-]+$/gu, "")
    .replace(/\s+/gu, " ")
    .trim();

const extractEventNameFromText = (value: unknown, knownStandNumber?: string): string => {
  const normalizedValue = getString(value);

  if (!normalizedValue) {
    return "";
  }

  const standNumber = knownStandNumber || extractStandNumberFromText(normalizedValue);

  if (!standNumber) {
    return "";
  }

  const standPattern = new RegExp(`\\b${escapeRegExp(standNumber)}\\b`, "iu");
  const candidateLine = normalizedValue
    .split(/\r?\n/gu)
    .map((line) => line.trim())
    .find((line) => standPattern.test(line));

  if (!candidateLine) {
    return "";
  }

  const [ beforeStand = "" ] = candidateLine.split(standPattern);
  const normalizedCandidate = normalizeEventNameCandidate(beforeStand);

  return normalizedCandidate.length >= 3 && normalizedCandidate.length <= 80
    ? normalizedCandidate
    : "";
};

const getDirectStandNumber = (record: Record<string, unknown>, keys: string[]): string =>
  keys
    .map((key) => extractStandNumber(record[ key ]))
    .find(Boolean) || "";

const uniqueValues = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

const collectNestedPayloads = (value: unknown, maxDepth = 6, depth = 0): unknown[] => {
  if (depth > maxDepth || value === null || value === undefined) {
    return [];
  }

  const normalizedValue = normalizeMixedJsonValue(value);

  if (Array.isArray(normalizedValue)) {
    return normalizedValue.flatMap((item) => collectNestedPayloads(item, maxDepth, depth + 1));
  }

  if (!isRecord(normalizedValue)) {
    return [ normalizedValue ];
  }

  return [
    normalizedValue,
    ...Object.values(normalizedValue).flatMap((item) => collectNestedPayloads(item, maxDepth, depth + 1)),
  ];
};

const normalizeAiAction = (value: unknown): TCommunicationRequestItem["action"] | undefined => {
  const normalizedValue = getString(value).toLowerCase();

  if (!normalizedValue) {
    return undefined;
  }

  if ([ "question", "clarification", "follow_up", "followup" ].includes(normalizedValue)) {
    return "question";
  }

  if ([ "add", "added", "include", "included", "book", "ordered", "order" ].includes(normalizedValue)) {
    return "add";
  }

  if ([ "remove", "removed", "delete", "deleted", "cancel", "cancelled" ].includes(normalizedValue)) {
    return "remove";
  }

  if ([ "change", "changed", "update", "updated", "adjust", "replace", "modify" ].includes(normalizedValue)) {
    return "change";
  }

  return undefined;
};

const inferAiActionFromText = (
  itemName: string,
  notes: string,
  questionText: string,
  intent?: string | null
): TCommunicationRequestItem["action"] | undefined => {
  if (questionText) {
    return "question";
  }

  const combinedText = `${itemName} ${notes}`.toLowerCase();

  if (/\b(remove|delete|without|cancel|drop)\b/u.test(combinedText)) {
    return "remove";
  }

  if (/\b(change|adjust|replace|move|update|modify)\b/u.test(combinedText)) {
    return "change";
  }

  if (/\b(add|need|include|plus|book|order)\b/u.test(combinedText)) {
    return "add";
  }

  if (intent === "question") {
    return "question";
  }

  if (intent === "update") {
    return "change";
  }

  if (intent === "order") {
    return "add";
  }

  return undefined;
};

const normalizeAiQuantity = (
  value: number | undefined,
  action?: TCommunicationRequestItem["action"]
): number => {
  if (typeof value !== "number" || Number.isNaN(value) || value === 0) {
    return 1;
  }

  if (action === "remove") {
    return Math.abs(Math.round(value));
  }

  return Math.max(1, Math.round(Math.abs(value)));
};

const looksLikeAiCandidateRecord = (record: Record<string, unknown>): boolean => {
  const action = normalizeAiAction(getDirectString(record, AI_ACTION_KEYS));
  const itemName = getDirectString(record, AI_ITEM_KEYS);
  const questionText = getDirectString(record, AI_QUESTION_KEYS);
  const qty = getDirectNumber(record, AI_QTY_KEYS);
  const standNumber = getDirectStandNumber(record, AI_STAND_KEYS);

  return Boolean(action || itemName || questionText || typeof qty === "number" || standNumber);
};

const collectAiCandidateRecords = (value: unknown): Record<string, unknown>[] =>
  collectNestedPayloads(value)
    .filter((item): item is Record<string, unknown> => isRecord(item))
    .filter((record) => looksLikeAiCandidateRecord(record));

const nestConditions = (
  conditions: Array<TCommunicationWhere | undefined>,
  condition: "AND" | "OR"
): TCommunicationWhere | undefined => {
  const filteredConditions = conditions.filter(Boolean) as TCommunicationWhere[];

  if (!filteredConditions.length) {
    return undefined;
  }

  const [ currentCondition, ...rest ] = filteredConditions;

  if (!rest.length) {
    return currentCondition;
  }

  return {
    ...currentCondition,
    [ condition ]: [ nestConditions(rest, condition) as TCommunicationWhere ],
  };
};

const buildLikeValue = (value: string): string => `%${value.trim()}%`;

export const buildCommunicationIncomingWhere = (
  filters: TCommunicationFilters,
  incomingIds?: string[]
): TCommunicationWhere | undefined => {
  const conditions: Array<TCommunicationWhere | undefined> = [];

  if (incomingIds?.length) {
    conditions.push({
      column: "ID",
      operator: "IN",
      value: incomingIds,
    });
  }

  if (filters.sender.trim()) {
    conditions.push(
      nestConditions(
        [
          {
            column: "FROM_EMAIL",
            operator: "LIKE",
            value: buildLikeValue(filters.sender),
          },
          {
            column: "FROM_NAME",
            operator: "LIKE",
            value: buildLikeValue(filters.sender),
          },
        ],
        "OR"
      )
    );
  }

  if (filters.received_date) {
    conditions.push({
      column: "RECEIVED_AT",
      operator: "GTE",
      value: `${filters.received_date}T00:00:00`,
    });
    conditions.push({
      column: "RECEIVED_AT",
      operator: "LTE",
      value: `${filters.received_date}T23:59:59`,
    });
  }

  return nestConditions(conditions, "AND");
};

const hasRequestLevelFilters = (filters: TCommunicationFilters): boolean =>
  REQUEST_FILTER_FIELDS.some((field) => Boolean(filters[ field ]?.trim()));

const readNestedFieldString = (value: unknown, keys: string[], depth = 0): string => {
  if (depth > 5 || value === null || value === undefined) {
    return "";
  }

  const normalizedValue = normalizeMixedJsonValue(value);

  if (typeof normalizedValue === "string") {
    return depth === 0 ? normalizedValue.trim() : "";
  }

  if (Array.isArray(normalizedValue)) {
    for (const item of normalizedValue) {
      const nestedValue = readNestedFieldString(item, keys, depth + 1);

      if (nestedValue) {
        return nestedValue;
      }
    }

    return "";
  }

  if (!isRecord(normalizedValue)) {
    return "";
  }

  const directValue = getDirectString(normalizedValue, keys);

  if (directValue) {
    return directValue;
  }

  for (const nestedValue of Object.values(normalizedValue)) {
    const extractedValue = readNestedFieldString(nestedValue, keys, depth + 1);

    if (extractedValue) {
      return extractedValue;
    }
  }

  return "";
};

const extractAiFallbackContext = (
  values: unknown[],
  incoming?: TRawEmailIncoming
) => {
  const standNumber =
    values
      .map((value) => extractStandNumber(readNestedFieldString(value, AI_STAND_KEYS)))
      .find(Boolean)
    || extractStandNumberFromText(incoming?.subject)
    || extractStandNumberFromText(incoming?.header_meta?.subject)
    || extractStandNumberFromText(incoming?.body_clean);

  return {
    eventName:
      values
        .map((value) => readNestedFieldString(value, AI_EVENT_KEYS))
        .find(Boolean)
      || extractEventNameFromText(incoming?.subject, standNumber)
      || extractEventNameFromText(incoming?.header_meta?.subject, standNumber)
      || extractEventNameFromText(incoming?.body_clean, standNumber)
      || "",
    companyName:
      values
        .map((value) => readNestedFieldString(value, AI_COMPANY_KEYS))
        .find(Boolean)
      || "",
    standNumber,
  };
};

const getAiCandidateTitle = (record: Record<string, unknown>): string => {
  const candidates = record.candidates;

  if (!Array.isArray(candidates)) {
    return "";
  }

  const primaryCandidate = candidates.find((candidate) => isRecord(candidate));

  return primaryCandidate && isRecord(primaryCandidate)
    ? getDirectString(primaryCandidate, [ "name", "title", "label" ])
    : "";
};

const getAiTopCandidate = (
  record: Record<string, unknown>
): { id: string; title: string; score?: number } | undefined => {
  const candidates = record.candidates;

  if (!Array.isArray(candidates)) {
    return undefined;
  }

  const primaryCandidate = candidates.find((candidate) => isRecord(candidate));

  if (!primaryCandidate || !isRecord(primaryCandidate)) {
    return undefined;
  }

  const candidateId = pickFirstScalarString(primaryCandidate.id);
  const candidateTitle = getDirectString(primaryCandidate, [ "name", "title", "label" ]);
  const candidateScore = getDirectNumber(primaryCandidate, [ "score", "confidence" ]);

  if (!candidateId) {
    return undefined;
  }

  return {
    id: candidateId,
    title: candidateTitle || candidateId,
    score: candidateScore,
  };
};

const shouldUseAiTopCandidateFallback = (
  record: Record<string, unknown>,
  topCandidate?: { id: string; title: string; score?: number }
): boolean => {
  if (!topCandidate?.id) {
    return false;
  }

  const matchScore = getDirectNumber(record, [ "match_score" ]);
  const itemConfidence = getDirectNumber(record, [ "item_confidence" ]);

  if (typeof matchScore === "number" && matchScore >= 0.5) {
    return true;
  }

  if (typeof itemConfidence === "number" && itemConfidence >= 0.45) {
    return true;
  }

  return typeof topCandidate.score === "number" && topCandidate.score >= 0.5;
};

const buildMergedAiCandidateRecords = (
  parsedPayload: unknown,
  dataPayload: unknown
): Record<string, unknown>[] => {
  const parsedItems = getAiItemRecords(parsedPayload);
  const dataItems = getAiItemRecords(dataPayload);
  const parsedQuestions = getAiQuestionRecords(parsedPayload);
  const dataQuestions = getAiQuestionRecords(dataPayload);

  if (!parsedItems.length && !dataItems.length && !parsedQuestions.length && !dataQuestions.length) {
    return uniqueValues([ "parsed", "data" ])
      .flatMap((source) =>
        collectAiCandidateRecords(source === "parsed" ? parsedPayload : dataPayload)
      );
  }

  const maxItemsCount = Math.max(parsedItems.length, dataItems.length);
  const mergedRecords: Record<string, unknown>[] = [];

  for (let index = 0; index < maxItemsCount; index += 1) {
    const parsedItem = parsedItems[ index ];
    const dataItem = dataItems[ index ];

    if (!parsedItem && !dataItem) {
      continue;
    }

    mergedRecords.push({
      ...(dataItem || {}),
      ...(parsedItem || {}),
    });
  }

  const maxQuestionsCount = Math.max(parsedQuestions.length, dataQuestions.length);

  for (let index = 0; index < maxQuestionsCount; index += 1) {
    const parsedQuestion = parsedQuestions[ index ];
    const dataQuestion = dataQuestions[ index ];

    if (!parsedQuestion && !dataQuestion) {
      continue;
    }

    mergedRecords.push({
      ...(dataQuestion || {}),
      ...(parsedQuestion || {}),
    });
  }

  const rootQuestionRecord =
    [ getAiRootRecord(parsedPayload), getAiRootRecord(dataPayload) ]
      .find((record) => record && Boolean(getDirectString(record, AI_QUESTION_KEYS)));

  if (rootQuestionRecord) {
    mergedRecords.push(rootQuestionRecord);
  }

  return mergedRecords.filter((record) => looksLikeAiCandidateRecord(record));
};

const getAiResponseQualityScore = (
  response: TRawEmailAiResponse,
  parsedPayload: unknown,
  dataPayload: unknown,
  incoming: TRawEmailIncoming
): number => {
  const context = extractAiFallbackContext([ parsedPayload, dataPayload ], incoming);
  const parsedItems = getAiItemRecords(parsedPayload);
  const dataItems = getAiItemRecords(dataPayload);
  const hasParsedPayload = hasUsableAiPayload(parsedPayload);
  const hasDataPayload = hasUsableAiPayload(dataPayload);
  const matchedItemsCount = dataItems.filter((item) =>
    Boolean(
      pickFirstScalarString(
        item.equipment_group_id,
        item.stand_id,
        item.ee_id
      )
    )
  ).length;
  const reviewedMatchesCount = dataItems.filter((item) => item.needs_review === false).length;
  const questionText = pickFirstNonEmpty(
    readNestedFieldString(parsedPayload, AI_QUESTION_KEYS),
    readNestedFieldString(dataPayload, AI_QUESTION_KEYS)
  );

  return (
    (context.eventName ? 100 : 0)
    + (context.companyName ? 100 : 0)
    + (context.standNumber ? 200 : 0)
    + (hasDataPayload ? 40 : 0)
    + (hasParsedPayload ? 20 : 0)
    + matchedItemsCount * 30
    + reviewedMatchesCount * 10
    + dataItems.length * 8
    + parsedItems.length * 4
    + (questionText ? 12 : 0)
    + (response.needs_review === false ? 12 : 0)
    + normalizeConfidence(response.confidence)
  );
};

const getBestAiPayload = (
  responses: TRawEmailAiResponse[],
  incoming: TRawEmailIncoming
): TSelectedAiPayload | undefined => {
  const normalizedResponses = [ ...responses ]
    .map((response) => {
      const parsedPayload = getAiParsedPayload(response);
      const dataPayload = getAiDataPayload(response);

      return {
        response,
        parsedPayload,
        dataPayload,
        qualityScore: getAiResponseQualityScore(response, parsedPayload, dataPayload, incoming),
        statusRank:
          response.status === "success"
            ? 0
            : response.status === "processing"
              ? 1
              : response.status === "retry"
                ? 2
                : 3,
        updatedAt: new Date(
          pickFirstNonEmpty(response.updated_at, response.created_at) || 0
        ).getTime(),
      };
    })
    .filter(({ parsedPayload, dataPayload, response }) =>
      (hasUsableAiPayload(parsedPayload) || hasUsableAiPayload(dataPayload))
      && response.status !== "error"
    )
    .sort((left, right) => {
      if (left.statusRank !== right.statusRank) {
        return left.statusRank - right.statusRank;
      }

      if (left.qualityScore !== right.qualityScore) {
        return right.qualityScore - left.qualityScore;
      }

      return right.updatedAt - left.updatedAt;
    });

  return normalizedResponses[ 0 ];
};

const getSelectedAiQuestionText = (
  selectedAiPayload?: TSelectedAiPayload
): string =>
  selectedAiPayload
    ? pickFirstNonEmpty(
      readNestedFieldString(selectedAiPayload.parsedPayload, AI_QUESTION_KEYS),
      readNestedFieldString(selectedAiPayload.dataPayload, AI_QUESTION_KEYS)
    )
    : "";

const buildDerivedAiMetadata = (
  incoming: TRawEmailIncoming,
  selectedAiPayload?: TSelectedAiPayload
): TDerivedAiMetadata => ({
  aiSelectedResponseId: pickFirstNonEmpty(selectedAiPayload?.response.id) || undefined,
  aiProvider: pickFirstNonEmpty(selectedAiPayload?.response.provider) || undefined,
  aiModel: pickFirstNonEmpty(selectedAiPayload?.response.model) || undefined,
  aiAttempt:
    typeof selectedAiPayload?.response.attempt === "number"
      ? selectedAiPayload.response.attempt
      : undefined,
  aiResponseStatus: selectedAiPayload?.response.status || undefined,
  aiNeedsReview:
    typeof selectedAiPayload?.response.needs_review === "boolean"
      ? selectedAiPayload.response.needs_review
      : null,
  aiResponseConfidence:
    typeof selectedAiPayload?.response.confidence === "number"
      ? normalizeConfidence(selectedAiPayload.response.confidence)
      : null,
  aiResponseTimeMs:
    typeof selectedAiPayload?.response.response_time_ms === "number"
      ? selectedAiPayload.response.response_time_ms
      : null,
  aiResponseCountTotal:
    typeof incoming.getAiResponseCount?.total === "number"
      ? incoming.getAiResponseCount.total
      : null,
  aiResponseCountProcessing:
    typeof incoming.getAiResponseCount?.processing === "number"
      ? incoming.getAiResponseCount.processing
      : null,
  aiHasParsedResponse: hasUsableAiPayload(selectedAiPayload?.parsedPayload),
  aiHasDataResponse: hasUsableAiPayload(selectedAiPayload?.dataPayload),
  aiQuestionText: getSelectedAiQuestionText(selectedAiPayload) || undefined,
});

const buildAiFallbackSignature = (
  action: TCommunicationRequestItem["action"],
  eventName: string,
  companyName: string,
  standNumber: string,
  itemName: string,
  itemNotes: string,
  quantity: number
): string => [
  action,
  eventName,
  companyName,
  standNumber,
  itemName,
  itemNotes,
  quantity,
].join("|");

const buildAiFallbackData = (
  incoming: TRawEmailIncoming,
  aiResponses: TRawEmailAiResponse[],
  selectedAiPayload?: TSelectedAiPayload
): TNormalizedAiFallback | undefined => {
  const selectedAiResponse = selectedAiPayload || getBestAiPayload(aiResponses, incoming);

  if (!selectedAiResponse) {
    return undefined;
  }

  const { response, parsedPayload, dataPayload } = selectedAiResponse;
  const rootContext = extractAiFallbackContext([ parsedPayload, dataPayload ], incoming);
  const derivedAiMetadata = buildDerivedAiMetadata(incoming, selectedAiResponse);
  const candidateRecords = buildMergedAiCandidateRecords(parsedPayload, dataPayload);
  const candidateStandNumbers = uniqueValues(
    candidateRecords
      .map((record) => getDirectStandNumber(record, AI_STAND_KEYS))
      .filter(Boolean)
  );
  const candidateCompanyNames = uniqueValues(
    candidateRecords
      .map((record) => getDirectString(record, AI_COMPANY_KEYS))
      .filter(Boolean)
  );
  const signatures = new Set<string>();
  const requestItems: TCommunicationRequestItem[] = [];

  candidateRecords.forEach((record, index) => {
    const itemName = getDirectString(record, AI_ITEM_KEYS);
    const questionText = getDirectString(record, AI_QUESTION_KEYS);
    const itemNotes = getDirectString(record, AI_NOTES_KEYS);
    const action = normalizeAiAction(getDirectString(record, AI_ACTION_KEYS))
      || inferAiActionFromText(itemName, itemNotes, questionText, response.intent || incoming.intent);

    if (!action) {
      return;
    }

    const directEventName = getDirectString(record, AI_EVENT_KEYS);
    const directCompanyName = getDirectString(record, AI_COMPANY_KEYS);
    const directStandNumber = getDirectStandNumber(record, AI_STAND_KEYS);
    const hasDirectScopedContext = Boolean(directEventName || directCompanyName || directStandNumber);
    const hasAmbiguousStandContext =
      !directStandNumber
      && candidateStandNumbers.length > 1
      && (action === "question" || hasDirectScopedContext);
    const hasAmbiguousCompanyContext =
      !directCompanyName
      && candidateCompanyNames.length > 1
      && (action === "question" || hasDirectScopedContext);
    const eventName = directEventName || rootContext.eventName;
    const companyName =
      directCompanyName
      || (hasAmbiguousCompanyContext ? "" : rootContext.companyName);
    const standNumber =
      directStandNumber
      || (hasAmbiguousStandContext ? "" : rootContext.standNumber);
    const normalizedItemName = pickFirstNonEmpty(
      itemName,
      action === "question" ? questionText : "",
      itemNotes
    );
    const normalizedItemNotes = pickFirstNonEmpty(
      action === "question" ? questionText : "",
      itemNotes,
      normalizedItemName !== questionText ? questionText : ""
    );

    if (!normalizedItemName && !normalizedItemNotes) {
      return;
    }

    const itemQuantity = normalizeAiQuantity(getDirectNumber(record, AI_QTY_KEYS), action);
    const confidence = normalizeConfidence(
      getDirectNumber(record, AI_CONFIDENCE_KEYS) ?? response.confidence
    );
    const eventId = pickFirstScalarString(record.ee_id) || undefined;
    const standId = pickFirstScalarString(record.stand_id) || undefined;
    const equipmentGroupId = pickFirstScalarString(record.equipment_group_id) || undefined;
    const topCandidate = getAiTopCandidate(record);
    const candidateEquipmentId =
      !equipmentGroupId && shouldUseAiTopCandidateFallback(record, topCandidate)
        ? topCandidate?.id
        : undefined;
    const equipmentTitle = pickFirstNonEmpty(
      candidateEquipmentId ? topCandidate?.title : "",
      getAiCandidateTitle(record),
      normalizedItemName
    ) || undefined;
    const signature = buildAiFallbackSignature(
      action,
      eventName,
      companyName,
      standNumber,
      normalizedItemName,
      normalizedItemNotes,
      itemQuantity
    );

    if (signatures.has(signature)) {
      return;
    }

    signatures.add(signature);

    requestItems.push({
      id: `ai-request-${incoming.id}-${index}`,
      email_request_id: `ai-request-${incoming.id}-${index}`,
      email_incoming_id: pickFirstNonEmpty(incoming.id),
      event_name: eventName,
      event_id: eventId,
      company_name: companyName,
      stand_number: standNumber,
      stand_id: standId,
      action,
      status: action === "question" || equipmentGroupId || candidateEquipmentId ? "pending" : "unidentified",
      item_name: normalizedItemName,
      item_qty: itemQuantity,
      item_notes: normalizedItemNotes || undefined,
      confidence,
      equipment_title: equipmentTitle,
      equipment_id: candidateEquipmentId,
      equipment_group_id: equipmentGroupId,
      flooring_id: pickFirstScalarString(record.flooring_id) || undefined,
      processed_at: pickFirstNonEmpty(response.updated_at, response.created_at) || undefined,
      created_at: pickFirstNonEmpty(response.created_at) || undefined,
      updated_at: pickFirstNonEmpty(response.updated_at, response.created_at) || undefined,
    });
  });

  const normalizedRequestItems = normalizeRequestItemContexts(requestItems);

  const questions = normalizedRequestItems
    .filter((request) => request.action === "question")
    .map((request) => ({
      id: request.id,
      text: pickFirstNonEmpty(request.item_notes, request.item_name),
      status: "open" as const,
    }))
    .filter((question) => question.text);

  const equipmentChanges = normalizedRequestItems
    .filter((request) => request.action !== "question")
    .map((request): TCommunicationEquipmentChange => {
      const suggestedEquipmentGroupId = request.equipment_group_id || undefined;

      return {
        id: request.id,
        action: request.action as TCommunicationEquipmentChange["action"],
        item_label: request.item_name,
        event_name: request.event_name || undefined,
        company_name: request.company_name || undefined,
        stand_number: request.stand_number || undefined,
        equipment_title: request.equipment_title || request.item_name || undefined,
        notes: request.item_notes || undefined,
        qty: request.item_qty || 1,
        color: null,
        equipment_id: request.equipment_id || undefined,
        equipment_group_id: suggestedEquipmentGroupId,
        suggested_equipment_group_id: suggestedEquipmentGroupId,
        suggestion_status: suggestedEquipmentGroupId ? "pending" : undefined,
      };
    })
    .filter((change) => change.item_label);

  const requestGroups = buildRequestGroups(normalizedRequestItems);
  const standNumbers = getNormalizedStandNumbers([
    rootContext.standNumber,
    ...requestGroups.map((group) => group.stand_number),
  ]);
  const eventName = pickFirstNonEmpty(
    rootContext.eventName,
    ...requestGroups.map((group) => group.event_name)
  );
  const companyName = pickFirstNonEmpty(
    rootContext.companyName,
    ...requestGroups.map((group) => group.company_name)
  );
  const eventId = pickFirstNonEmpty(...normalizedRequestItems.map((request) => request.event_id)) || undefined;
  const standNumber = pickFirstNonEmpty(
    rootContext.standNumber,
    ...requestGroups.map((group) => group.stand_number)
  );
  const standId = pickFirstNonEmpty(...normalizedRequestItems.map((request) => request.stand_id)) || undefined;
  const finalChanges = createFinalChanges(equipmentChanges, {}, "");
  const hasUsefulFallback =
    Boolean(eventName || companyName || standNumbers.length > 0)
    || normalizedRequestItems.length > 0
    || questions.length > 0;

  if (!hasUsefulFallback) {
    return undefined;
  }

  return {
    requestItems: normalizedRequestItems,
    requestGroups,
    questions,
    finalChanges,
    eventName,
    eventId,
    companyName,
    standNumber,
    standNumbers,
    standId,
    confidence: Math.max(
      normalizeConfidence(response.confidence),
      ...requestItems.map((request) => request.confidence)
    ),
    parserProcessedAt: getLatestDate([
      response.updated_at,
      response.created_at,
      incoming.parsed_at,
    ]),
    sourceIntent: pickFirstNonEmpty(response.intent, incoming.intent) || undefined,
    aiQuestionText: derivedAiMetadata.aiQuestionText,
    aiSelectedResponseId: derivedAiMetadata.aiSelectedResponseId,
    aiProvider: derivedAiMetadata.aiProvider,
    aiModel: derivedAiMetadata.aiModel,
    aiAttempt: derivedAiMetadata.aiAttempt,
    aiResponseStatus: derivedAiMetadata.aiResponseStatus,
    aiNeedsReview: derivedAiMetadata.aiNeedsReview,
    aiResponseConfidence: derivedAiMetadata.aiResponseConfidence,
    aiResponseTimeMs: derivedAiMetadata.aiResponseTimeMs,
    aiResponseCountTotal: derivedAiMetadata.aiResponseCountTotal,
    aiResponseCountProcessing: derivedAiMetadata.aiResponseCountProcessing,
    aiHasParsedResponse: derivedAiMetadata.aiHasParsedResponse,
    aiHasDataResponse: derivedAiMetadata.aiHasDataResponse,
  };
};

const appendAiQuestionFallback = (
  questions: TCommunicationQuestion[],
  aiQuestionText: string | null | undefined,
  incomingId: string
): TCommunicationQuestion[] => {
  const normalizedAiQuestion = getString(aiQuestionText);

  if (!normalizedAiQuestion) {
    return questions;
  }

  const hasMatchingQuestion = questions.some((question) =>
    getString(question.text).toLowerCase() === normalizedAiQuestion.toLowerCase()
  );

  if (hasMatchingQuestion) {
    return questions;
  }

  return [
    ...questions,
    {
      id: `ai-question-${incomingId}`,
      text: normalizedAiQuestion,
      status: "open",
    },
  ];
};

const getLatestDate = (values: Array<string | null | undefined>): string | undefined => {
  const normalizedValues = uniqueValues(values.map((value) => getString(value)));

  if (!normalizedValues.length) {
    return undefined;
  }

  return normalizedValues.sort(
    (left, right) => new Date(right).getTime() - new Date(left).getTime()
  )[ 0 ];
};

const getNormalizedStandNumbers = (values: Array<string | null | undefined>): string[] =>
  uniqueValues(
    values
      .map((value) => getString(value))
      .filter(Boolean)
  );

const buildContextGroupKey = (
  eventName: string,
  companyName: string,
  standNumber: string
): string => [ eventName || "unassigned", companyName || "unassigned", standNumber || "unassigned" ].join("|");

const buildContextGroups = (
  requestGroups: TCommunicationRequestGroup[],
  fallback?: {
    event_name?: string | null;
    event_id?: string | null;
    company_name?: string | null;
    company_id?: string | null;
    stand_number?: string | null;
    stand_id?: string | null;
  }
): TCommunicationContextGroup[] => {
  const groupedContexts = requestGroups
    .map((group) => ({
      id: group.id || buildContextGroupKey(group.event_name, group.company_name, group.stand_number),
      event_name: getString(group.event_name),
      event_id: pickFirstNonEmpty(group.event_id) || undefined,
      company_name: getString(group.company_name),
      company_id: undefined,
      stand_number: getString(group.stand_number),
      stand_id: pickFirstNonEmpty(group.stand_id) || undefined,
      cad_id: pickFirstNonEmpty(group.cad_id) || undefined,
      stand_drawing_preview_url: pickFirstNonEmpty(group.stand_drawing_preview_url) || undefined,
      stand_drawing_file_path: pickFirstNonEmpty(group.stand_drawing_file_path) || undefined,
    }))
    .filter((group) => group.event_name || group.company_name || group.stand_number);

  if (groupedContexts.length > 0) {
    return groupedContexts;
  }

  const fallbackGroup = {
    id: buildContextGroupKey(
      getString(fallback?.event_name),
      getString(fallback?.company_name),
      getString(fallback?.stand_number)
    ),
    event_name: getString(fallback?.event_name),
    event_id: pickFirstNonEmpty(fallback?.event_id) || undefined,
    company_name: getString(fallback?.company_name),
    company_id: pickFirstNonEmpty(fallback?.company_id) || undefined,
    stand_number: getString(fallback?.stand_number),
    stand_id: pickFirstNonEmpty(fallback?.stand_id) || undefined,
    cad_id: undefined,
    stand_drawing_preview_url: undefined,
    stand_drawing_file_path: undefined,
  };

  return fallbackGroup.event_name || fallbackGroup.company_name || fallbackGroup.stand_number
    ? [ fallbackGroup ]
    : [];
};

const getPrimaryContextGroup = (
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup | undefined =>
  contextGroups.find((group) => group.event_name && group.company_name && group.stand_number)
  || contextGroups.find((group) => group.event_name || group.company_name || group.stand_number);

const areContextGroupsComplete = (
  contextGroups: TCommunicationContextGroup[]
): boolean =>
  contextGroups.length > 0
  && contextGroups.every((group) =>
    Boolean(group.event_name.trim() && group.company_name.trim() && group.stand_number.trim())
  );

const DEMO_MULTI_STAND_TASK_ID = "demo-multi-stand-email";

const includesNormalized = (source: string | null | undefined, search: string): boolean =>
  getString(source).toLowerCase().includes(search.toLowerCase().trim());

const matchesClientSideFilters = (
  task: TCommunicationTask,
  filters: TCommunicationFilters
): boolean => {
  const eventSearch = filters.event_name.trim().toLowerCase();
  const companySearch = filters.company_name.trim().toLowerCase();
  const standSearch = filters.stand_number.trim().toLowerCase();
  const senderSearch = filters.sender.trim().toLowerCase();

  if (
    senderSearch
    && ![
      task.sender,
      task.sender_email || "",
    ].some((value) => includesNormalized(value, senderSearch))
  ) {
    return false;
  }

  if (
    eventSearch
    && ![
      task.event_name,
      ...task.context_groups.map((group) => group.event_name),
      ...task.request_items.map((request) => request.event_name),
    ].some((value) => includesNormalized(value, eventSearch))
  ) {
    return false;
  }

  if (
    companySearch
    && ![
      task.company_name,
      ...task.context_groups.map((group) => group.company_name),
      ...task.request_items.map((request) => request.company_name),
    ].some((value) => includesNormalized(value, companySearch))
  ) {
    return false;
  }

  if (
    standSearch
    && ![
      task.stand_number,
      ...task.context_groups.map((group) => group.stand_number),
      ...task.request_items.map((request) => request.stand_number),
    ].some((value) => includesNormalized(value, standSearch))
  ) {
    return false;
  }

  if (
    filters.received_date
    && getCommunicationDayKey(task.received_at) !== filters.received_date
  ) {
    return false;
  }

  return true;
};

export const getTaskStatusFromValues = (
  values: Pick<TCommunicationTaskFormValues, "event_name" | "company_name" | "stand_number" | "stand_numbers" | "context_groups">,
  isManagerApproved = false
): TCommunicationTask["status"] => {
  if (isManagerApproved) {
    return CommunicationConst.statuses.APPROVED;
  }

  if (values.context_groups?.length) {
    return areContextGroupsComplete(values.context_groups)
      ? CommunicationConst.statuses.PENDING
      : CommunicationConst.statuses.UNIDENTIFIED;
  }

  const standNumbers = getNormalizedStandNumbers([
    ...(values.stand_numbers || []),
    values.stand_number,
  ]);

  return values.event_name?.trim() && values.company_name?.trim() && standNumbers.length > 0
    ? CommunicationConst.statuses.PENDING
    : CommunicationConst.statuses.UNIDENTIFIED;
};

const buildContextGroupLookupKey = (
  eventName?: string | null,
  companyName?: string | null,
  standNumber?: string | null
): string => [ getString(eventName), getString(companyName), getString(standNumber) ].join("|");

const getExcludedContextGroups = (task: TCommunicationTask): TCommunicationContextGroup[] => {
  const activeContextKeys = new Set(
    (task.context_groups || []).map((group) =>
      buildContextGroupLookupKey(group.event_name, group.company_name, group.stand_number)
    )
  );

  const originalGroups = task.request_groups.length
    ? task.request_groups.map((group) => ({
      id: group.id,
      event_name: group.event_name,
      company_name: group.company_name,
      stand_number: group.stand_number,
    }))
    : [];

  return originalGroups.filter((group) =>
    !activeContextKeys.has(buildContextGroupLookupKey(group.event_name, group.company_name, group.stand_number))
  );
};

const patchTaskWithFormValues = (
  task: TCommunicationTask,
  values: TCommunicationTaskFormValues,
  options?: {
    touchedAt?: string;
    assignee?: string;
    isApproved?: boolean;
  }
): TCommunicationTask => {
  const finalChanges = mapFormValuesToFinalChanges(values);
  const touchedAt = options?.touchedAt || new Date().toISOString();
  const isApproved = Boolean(options?.isApproved);
  const normalizedContextGroups = (values.context_groups || [])
    .map((group, index) => ({
      id: getString(group.id) || `context-${task.id}-${index}`,
      event_name: getString(group.event_name),
      company_name: getString(group.company_name),
      stand_number: getString(group.stand_number),
    }))
    .filter((group) => group.event_name || group.company_name || group.stand_number);
  const primaryContextGroup = getPrimaryContextGroup(normalizedContextGroups);
  const standNumbers = getNormalizedStandNumbers([
    ...normalizedContextGroups.map((group) => group.stand_number),
    ...(values.stand_numbers || []),
    values.stand_number,
  ]);
  const primaryStandNumber =
    getString(primaryContextGroup?.stand_number) || standNumbers[0] || getString(values.stand_number);

  return {
    ...task,
    event_name: getString(primaryContextGroup?.event_name) || getString(values.event_name),
    company_name: getString(primaryContextGroup?.company_name) || getString(values.company_name),
    stand_number: primaryStandNumber,
    stand_numbers: standNumbers,
    context_groups: normalizedContextGroups,
    organizer_id: values.organizer_id || undefined,
    exhibition_type_id: values.exhibition_type_id || undefined,
    questions: values.questions || [],
    final_changes: finalChanges,
    status: getTaskStatusFromValues(values, isApproved),
    updated_at: touchedAt,
    work_state: {
      ...task.work_state,
      status: isApproved ? "approved" : "in_progress",
      is_placeholder: true,
      assignee: options?.assignee || task.work_state.assignee || undefined,
      touched_at: touchedAt,
      viewed_at: task.work_state.viewed_at || touchedAt,
    },
  };
};

const getPrimaryRequest = (requests: TRawEmailRequest[]): TRawEmailRequest | undefined =>
  [ ...requests ].sort((left, right) => {
    const leftCompleteness = [
      pickFirstNonEmpty(left.exhibition_name, left.relExhibitionEvent?.title),
      pickFirstNonEmpty(left.company_name, left.relStand?.company_name),
      pickFirstNonEmpty(left.stand_number, left.relStand?.stand_number),
    ].filter(Boolean).length;
    const rightCompleteness = [
      pickFirstNonEmpty(right.exhibition_name, right.relExhibitionEvent?.title),
      pickFirstNonEmpty(right.company_name, right.relStand?.company_name),
      pickFirstNonEmpty(right.stand_number, right.relStand?.stand_number),
    ].filter(Boolean).length;

    if (rightCompleteness !== leftCompleteness) {
      return rightCompleteness - leftCompleteness;
    }

    return normalizeConfidence(right.confidence) - normalizeConfidence(left.confidence);
  })[ 0 ];

const getPrimaryFlooringRequest = (requests: TRawEmailRequest[]): TRawEmailRequest | undefined =>
  requests.find((request) => getString(request.flooring_id) || getString(request.relFlooring?.id));

const mapQuestionStatus = (
  status: TRawEmailRequest["status"]
): TCommunicationQuestion["status"] => (status === "APPROVED" ? "reviewed" : "open");

const mapRequestStatus = (
  status: TRawEmailRequest["status"]
): TCommunicationRequestItem["status"] => {
  if (status === "APPROVED") {
    return "approved";
  }

  if (status === "REJECTED") {
    return "rejected";
  }

  if (status === "CANCELLED") {
    return "cancelled";
  }

  if (status === "UNIDENTIFIED") {
    return "unidentified";
  }

  return "pending";
};

const toLowercaseAction = (
  action: TRawEmailRequest["action"]
): TCommunicationEquipmentChange["action"] => {
  if (action === "REMOVE") {
    return "remove";
  }

  if (action === "CHANGE") {
    return "change";
  }

  return "add";
};

const mapRequestAction = (
  action: TRawEmailRequest["action"]
): TCommunicationRequestItem["action"] => {
  if (action === "QUESTION") {
    return "question";
  }

  return toLowercaseAction(action);
};

const buildTaskCapabilities = (): TCommunicationTaskCapabilities => ({
  open_in_mystand: {
    enabled: false,
    mode: "concept",
    note_key: "COMMUNICATION.Concept_OpenInMyStandNote",
    href: null,
  },
  reply_to_customer: {
    enabled: false,
    mode: "concept",
    note_key: "COMMUNICATION.Concept_ReplyNote",
    href: null,
  },
  work_state_persistence: {
    enabled: false,
    mode: "concept",
    note_key: "COMMUNICATION.Concept_WorkStateNote",
    href: null,
  },
});

const mapRequestToItem = (
  request: TRawEmailRequest,
  incoming: TRawEmailIncoming
): TCommunicationRequestItem => ({
  id: pickFirstNonEmpty(request.id, `request-${incoming.id}`),
  email_request_id: pickFirstNonEmpty(request.id, `request-${incoming.id}`),
  email_incoming_id: pickFirstNonEmpty(request.email_incoming_id, incoming.id),
  event_name: pickFirstNonEmpty(request.exhibition_name, request.relExhibitionEvent?.title),
  event_id: pickFirstNonEmpty(request.ee_id, request.relExhibitionEvent?.id) || undefined,
  company_name: pickFirstNonEmpty(request.company_name, request.relStand?.company_name),
  stand_number: pickFirstNonEmpty(request.stand_number, request.relStand?.stand_number),
  stand_id: pickFirstNonEmpty(request.stand_id, request.relStand?.id) || undefined,
  cad_id: pickFirstNonEmpty(request.relStand?.relCad?.id) || undefined,
  stand_drawing_preview_url: pickFirstNonEmpty(request.relStand?.relCad?.getFilePathPreview) || undefined,
  stand_drawing_file_path: pickFirstNonEmpty(request.relStand?.relCad?.file_path) || undefined,
  action: mapRequestAction(request.action),
  status: mapRequestStatus(request.status),
  item_name: pickFirstNonEmpty(request.item_name, request.relEquipment?.title, request.item_notes),
  item_qty: request.item_qty || 1,
  item_notes: pickFirstNonEmpty(request.item_notes, request.notes) || undefined,
  confidence: normalizeConfidence(request.confidence),
  equipment_title: pickFirstNonEmpty(request.relEquipment?.title) || undefined,
  equipment_id: pickFirstNonEmpty(request.equipment_id, request.relEquipment?.id) || undefined,
  equipment_group_id: pickFirstNonEmpty(request.relEquipment?.group_id) || undefined,
  flooring_id: pickFirstNonEmpty(request.flooring_id, request.relFlooring?.id) || undefined,
  processed_by_id: pickFirstNonEmpty(request.processed_by_id) || undefined,
  processed_at: pickFirstNonEmpty(request.processed_at) || undefined,
  created_at: pickFirstNonEmpty(request.created_at) || undefined,
  updated_at: pickFirstNonEmpty(request.updated_at) || undefined,
});

type TCommunicationRequestContextFields = Pick<
  TCommunicationRequestItem,
  | "event_name"
  | "event_id"
  | "company_name"
  | "stand_number"
  | "stand_id"
  | "cad_id"
  | "stand_drawing_preview_url"
  | "stand_drawing_file_path"
>;

const buildRequestContextFields = (
  request: TCommunicationRequestContextFields
): TCommunicationRequestContextFields => ({
  event_name: getString(request.event_name),
  event_id: pickFirstNonEmpty(request.event_id) || undefined,
  company_name: getString(request.company_name),
  stand_number: getString(request.stand_number),
  stand_id: pickFirstNonEmpty(request.stand_id) || undefined,
  cad_id: pickFirstNonEmpty(request.cad_id) || undefined,
  stand_drawing_preview_url: pickFirstNonEmpty(request.stand_drawing_preview_url) || undefined,
  stand_drawing_file_path: pickFirstNonEmpty(request.stand_drawing_file_path) || undefined,
});

const mergeRequestContextFields = (
  current?: TCommunicationRequestContextFields,
  next?: TCommunicationRequestContextFields
): TCommunicationRequestContextFields => ({
  event_name: pickFirstNonEmpty(current?.event_name, next?.event_name),
  event_id: pickFirstNonEmpty(current?.event_id, next?.event_id) || undefined,
  company_name: pickFirstNonEmpty(current?.company_name, next?.company_name),
  stand_number: pickFirstNonEmpty(current?.stand_number, next?.stand_number),
  stand_id: pickFirstNonEmpty(current?.stand_id, next?.stand_id) || undefined,
  cad_id: pickFirstNonEmpty(current?.cad_id, next?.cad_id) || undefined,
  stand_drawing_preview_url:
    pickFirstNonEmpty(current?.stand_drawing_preview_url, next?.stand_drawing_preview_url) || undefined,
  stand_drawing_file_path:
    pickFirstNonEmpty(current?.stand_drawing_file_path, next?.stand_drawing_file_path) || undefined,
});

const normalizeRequestContextTextToken = (value: string): string =>
  getString(value)
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[–—−]/gu, "-")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();

const normalizeRequestContextStandToken = (value: string): string =>
  getString(value)
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[–—−]/gu, "-")
    .replace(/[\s_/]+/gu, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/-+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .trim();

const buildRequestContextSignature = (
  request: Pick<TCommunicationRequestItem, "event_name" | "company_name" | "stand_number">
): string => {
  const normalizedEventName = normalizeRequestContextTextToken(request.event_name);
  const normalizedCompanyName = normalizeRequestContextTextToken(request.company_name);
  const normalizedStandNumber = normalizeRequestContextStandToken(request.stand_number);

  if (!normalizedEventName && !normalizedCompanyName && !normalizedStandNumber) {
    return "";
  }

  return [
    normalizedEventName || "unassigned",
    normalizedCompanyName || "unassigned",
    normalizedStandNumber || "unassigned",
  ].join("|");
};

// Align mixed AI/request payloads to one stable stand context before grouping.
const normalizeRequestItemContexts = (
  requestItems: TCommunicationRequestItem[]
): TCommunicationRequestItem[] => {
  if (requestItems.length < 2) {
    return requestItems;
  }

  const contextBySignature = new Map<string, TCommunicationRequestContextFields>();
  const contextByStandId = new Map<string, TCommunicationRequestContextFields>();

  requestItems.forEach((request) => {
    const requestContext = buildRequestContextFields(request);
    const signature = buildRequestContextSignature(request);
    const standId = getString(request.stand_id);

    if (signature) {
      contextBySignature.set(
        signature,
        mergeRequestContextFields(contextBySignature.get(signature), requestContext)
      );
    }

    if (standId) {
      contextByStandId.set(
        standId,
        mergeRequestContextFields(contextByStandId.get(standId), requestContext)
      );
    }
  });

  return requestItems.map((request) => {
    const signature = buildRequestContextSignature(request);
    const requestContext = buildRequestContextFields(request);
    const signatureContext = signature ? contextBySignature.get(signature) : undefined;
    const mergedWithSignature = mergeRequestContextFields(requestContext, signatureContext);
    const standContext = mergedWithSignature.stand_id
      ? contextByStandId.get(mergedWithSignature.stand_id)
      : undefined;
    const resolvedContext = mergeRequestContextFields(mergedWithSignature, standContext);

    return {
      ...request,
      event_name: resolvedContext.event_name,
      event_id: resolvedContext.event_id,
      company_name: resolvedContext.company_name,
      stand_number: resolvedContext.stand_number,
      stand_id: resolvedContext.stand_id,
      cad_id: resolvedContext.cad_id,
      stand_drawing_preview_url: resolvedContext.stand_drawing_preview_url,
      stand_drawing_file_path: resolvedContext.stand_drawing_file_path,
    };
  });
};

const buildRequestGroupKey = (request: TCommunicationRequestItem): string => {
  const normalizedStandId = getString(request.stand_id);

  if (normalizedStandId) {
    return `stand:${normalizedStandId}`;
  }

  const contextSignature = buildRequestContextSignature(request);

  return contextSignature ? `group:${contextSignature}` : `group:${request.id}`;
};

const buildRequestGroups = (
  requestItems: TCommunicationRequestItem[]
): TCommunicationRequestGroup[] =>
  Array.from(
    requestItems
      .reduce<Map<string, TCommunicationRequestGroup>>((groupsMap, request) => {
        const groupKey = buildRequestGroupKey(request);
        const existingGroup = groupsMap.get(groupKey);

        if (existingGroup) {
          existingGroup.requests.push(request);
          existingGroup.change_count += request.action === "question" ? 0 : 1;
          existingGroup.question_count += request.action === "question" ? 1 : 0;
          existingGroup.unidentified_count += request.status === "unidentified" ? 1 : 0;
          existingGroup.cad_id = existingGroup.cad_id || request.cad_id;
          existingGroup.stand_drawing_preview_url =
            existingGroup.stand_drawing_preview_url || request.stand_drawing_preview_url;
          existingGroup.stand_drawing_file_path =
            existingGroup.stand_drawing_file_path || request.stand_drawing_file_path;
          return groupsMap;
        }

        groupsMap.set(groupKey, {
          id: groupKey,
          event_name: request.event_name,
          event_id: request.event_id,
          company_name: request.company_name,
          stand_number: request.stand_number,
          stand_id: request.stand_id,
          cad_id: request.cad_id,
          stand_drawing_preview_url: request.stand_drawing_preview_url,
          stand_drawing_file_path: request.stand_drawing_file_path,
          requests: [ request ],
          change_count: request.action === "question" ? 0 : 1,
          question_count: request.action === "question" ? 1 : 0,
          unidentified_count: request.status === "unidentified" ? 1 : 0,
        });

        return groupsMap;
      }, new Map<string, TCommunicationRequestGroup>())
      .values()
  )
    .map((group) => ({
      ...group,
      requests: [ ...group.requests ].sort((left, right) => {
        if (left.action !== right.action) {
          return left.action === "question" ? 1 : -1;
        }

        return right.confidence - left.confidence;
      }),
    }))
    .sort((left, right) => {
      const leftStand = getString(left.stand_number);
      const rightStand = getString(right.stand_number);

      if (leftStand && rightStand && leftStand !== rightStand) {
        return leftStand.localeCompare(rightStand);
      }

      if (leftStand || rightStand) {
        return leftStand ? -1 : 1;
      }

      const leftCompany = getString(left.company_name);
      const rightCompany = getString(right.company_name);

      return `${leftCompany} ${left.event_name}`.localeCompare(`${rightCompany} ${right.event_name}`);
    });

const buildWorkState = (
  requests: TRawEmailRequest[],
  incoming: TRawEmailIncoming
): TCommunicationTaskWorkState => {
  const touchedAt = getLatestDate(
    requests.map((request) => pickFirstNonEmpty(request.processed_at, request.updated_at))
  );
  const hasProcessedRequest = requests.some(
    (request) => Boolean(getString(request.processed_at) || getString(request.processed_by_id))
  );
  const hasApprovedRequest = requests.length > 0
    && requests.every((request) => mapRequestStatus(request.status) === "approved");

  return {
    status: hasApprovedRequest
      ? "approved"
      : hasProcessedRequest
        ? "in_progress"
        : "untouched",
    is_placeholder: true,
    touched_at: touchedAt || pickFirstNonEmpty(incoming.parsed_at) || undefined,
    viewed_at: hasProcessedRequest || hasApprovedRequest
      ? touchedAt || pickFirstNonEmpty(incoming.parsed_at) || undefined
      : undefined,
    assignee: undefined,
  };
};

const createDemoMultiStandTask = (): TCommunicationTask => {
  const receivedAt = "2026-03-26T18:45:00";
  const primaryStandNumber = "OG-114";
  const secondaryStandNumber = "OG-127";
  const requestItems: TCommunicationRequestItem[] = [
    {
      id: "demo-request-1",
      email_request_id: "demo-request-1",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: primaryStandNumber,
      stand_id: "demo-stand-og-114",
      action: "add",
      status: "pending",
      item_name: "Textile wall graphic",
      item_qty: 10,
      item_notes: `Front wall print for stand ${primaryStandNumber}`,
      confidence: 86,
      equipment_title: "Textile wall graphic",
      equipment_group_id: "demo-group-graphic",
      created_at: receivedAt,
      updated_at: receivedAt,
    },
    {
      id: "demo-request-2",
      email_request_id: "demo-request-2",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: primaryStandNumber,
      stand_id: "demo-stand-og-114",
      action: "remove",
      status: "unidentified",
      item_name: "Halo panel",
      item_qty: 2,
      item_notes: "Please match this item to the catalog or delete it",
      confidence: 58,
      created_at: receivedAt,
      updated_at: receivedAt,
    },
    {
      id: "demo-request-3",
      email_request_id: "demo-request-3",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: secondaryStandNumber,
      stand_id: "demo-stand-og-127",
      action: "add",
      status: "pending",
      item_name: "Counter graphic panel",
      item_qty: 1,
      item_notes: `New counter branding for stand ${secondaryStandNumber}`,
      confidence: 74,
      equipment_title: "Counter graphic panel",
      equipment_group_id: "demo-group-counter",
      created_at: receivedAt,
      updated_at: receivedAt,
    },
    {
      id: "demo-request-4",
      email_request_id: "demo-request-4",
      email_incoming_id: DEMO_MULTI_STAND_TASK_ID,
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: secondaryStandNumber,
      stand_id: "demo-stand-og-127",
      action: "question",
      status: "pending",
      item_name: "Question about logo wall position",
      item_qty: 1,
      item_notes: `Can the logo wall for stand ${secondaryStandNumber} be moved to the back corner?`,
      confidence: 63,
      created_at: receivedAt,
      updated_at: receivedAt,
    },
  ];

  const requestGroups = buildRequestGroups(requestItems);
  const questions: TCommunicationQuestion[] = [
    {
      id: "demo-question-1",
      text: `Can the logo wall for stand ${secondaryStandNumber} be moved to the back corner?`,
      status: "open",
    },
  ];
  const equipmentChanges: TCommunicationEquipmentChange[] = [
    {
      id: "demo-change-1",
      action: "add",
      item_label: "Textile wall graphic",
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: primaryStandNumber,
      equipment_title: "Textile wall graphic",
      qty: 10,
      color: "Black",
      equipment_group_id: "demo-group-graphic",
      suggested_equipment_group_id: "demo-group-graphic",
      suggestion_status: "pending",
    },
    {
      id: "demo-change-2",
      action: "remove",
      item_label: "Halo panel",
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: primaryStandNumber,
      qty: 2,
      color: null,
    },
    {
      id: "demo-change-3",
      action: "add",
      item_label: "Counter graphic panel",
      event_name: "AAA Wels 2026",
      company_name: "Endress+Hauser Österreich",
      stand_number: secondaryStandNumber,
      equipment_title: "Counter graphic panel",
      qty: 1,
      color: "Black",
      equipment_group_id: "demo-group-counter",
      suggested_equipment_group_id: "demo-group-counter",
      suggestion_status: "pending",
    },
  ];
  const finalChanges = createFinalChanges(equipmentChanges, {}, "");
  const contextGroups = buildContextGroups(requestGroups, {
    event_name: "AAA Wels 2026",
    company_name: "Endress+Hauser Г–sterreich",
    stand_number: primaryStandNumber,
  });

  return {
    id: DEMO_MULTI_STAND_TASK_ID,
    source_email_id: DEMO_MULTI_STAND_TASK_ID,
    request_source: "email_requests",
    sender: "AAA Messen <aaa@messeprojekt.de>",
    sender_email: "aaa@messeprojekt.de",
    subject: `DEMO: AAA Wels 2026 multi-stand equipment request (${primaryStandNumber} + ${secondaryStandNumber})`,
    event_name: "AAA Wels 2026",
    stand_number: primaryStandNumber,
    stand_numbers: [ primaryStandNumber, secondaryStandNumber ],
    stand_id: "demo-stand-og-114",
    company_name: "Endress+Hauser Österreich",
    status: CommunicationConst.statuses.PENDING,
    confidence: 86,
    original_body:
      "Demo multi-stand case for UI validation.\n\n"
      + `Please add 10 textile wall graphics for stand ${primaryStandNumber}, remove 2 halo panels from stand ${primaryStandNumber}, `
      + `and add 1 counter graphic panel for stand ${secondaryStandNumber}. Also, can the logo wall for stand ${secondaryStandNumber} be moved to the back corner?`,
    ai_summary:
      "Demonstration email with parsed requests for two different stands of the same company.",
    ai_reason:
      "This synthetic example is kept in the board to validate the multi-stand email workflow before real backend cases arrive.",
    questions,
    request_items: requestItems,
    request_groups: requestGroups,
    context_groups: contextGroups,
    work_state: {
      status: "untouched",
      is_placeholder: true,
      touched_at: receivedAt,
      viewed_at: undefined,
      assignee: undefined,
    },
    capabilities: buildTaskCapabilities(),
    extracted_changes: finalChanges,
    final_changes: finalChanges,
    received_at: receivedAt,
    parser_processed_at: receivedAt,
    approved_at: undefined,
    approved_by: undefined,
    updated_at: receivedAt,
    demo_badge_key: "COMMUNICATION.Demo_MultiStandBadge",
    demo_note_key: "COMMUNICATION.Demo_MultiStandNote",
  };
};

const extractAiSummary = (
  incoming: TRawEmailIncoming,
  requests: TRawEmailRequest[],
  aiResponses: TRawEmailAiResponse[],
  selectedAiPayload?: TSelectedAiPayload
): string =>
  (selectedAiPayload
    ? pickFirstNonEmpty(
      readNestedFieldString(selectedAiPayload.parsedPayload, AI_SUMMARY_KEYS),
      readNestedFieldString(selectedAiPayload.dataPayload, AI_SUMMARY_KEYS)
    )
    : "")
  || readNestedFieldString(incoming.parsed_data, AI_SUMMARY_KEYS)
  || requests.map((request) => readNestedFieldString(request.ai_data, AI_SUMMARY_KEYS)).find(Boolean)
  || aiResponses
    .map((response) => resolveAiResponsePayload(response))
    .map((value) => readNestedFieldString(value, AI_SUMMARY_KEYS))
    .find(Boolean)
  || "";

const extractAiReason = (
  incoming: TRawEmailIncoming,
  requests: TRawEmailRequest[],
  aiResponses: TRawEmailAiResponse[],
  selectedAiPayload?: TSelectedAiPayload
): string =>
  getString(incoming.parse_error)
  || (selectedAiPayload
    ? pickFirstNonEmpty(
      readNestedFieldString(selectedAiPayload.parsedPayload, AI_REASON_KEYS),
      readNestedFieldString(selectedAiPayload.dataPayload, AI_REASON_KEYS)
    )
    : "")
  || readNestedFieldString(incoming.parsed_data, AI_REASON_KEYS)
  || requests.map((request) => readNestedFieldString(request.ai_data, AI_REASON_KEYS)).find(Boolean)
  || aiResponses
    .map((response) => resolveAiResponsePayload(response))
    .map((value) => readNestedFieldString(value, AI_REASON_KEYS))
    .find(Boolean)
  || "";

const mapIncomingToTask = (incoming: TRawEmailIncoming): TCommunicationTask => {
  const requests = incoming.relEmailRequests || [];
  const aiResponses = incoming.relEmailAiResponses || [];
  const selectedAiPayload = getBestAiPayload(aiResponses, incoming);
  const derivedAiMetadata = buildDerivedAiMetadata(incoming, selectedAiPayload);
  const aiFallbackData = requests.length === 0
    ? buildAiFallbackData(incoming, aiResponses, selectedAiPayload)
    : undefined;
  const originalEmailMeta = normalizeHeaderMeta(incoming.header_meta);
  const headerSenderValue = originalEmailMeta?.from || "";
  const requestItems = requests.length > 0
    ? normalizeRequestItemContexts(requests.map((request) => mapRequestToItem(request, incoming)))
    : aiFallbackData?.requestItems || [];
  const requestGroups = requests.length > 0
    ? buildRequestGroups(requestItems)
    : aiFallbackData?.requestGroups || [];
  const primaryRequest = getPrimaryRequest(requests);
  const flooringRequest = getPrimaryFlooringRequest(requests);
  const contextGroups = buildContextGroups(requestGroups, {
    event_name: aiFallbackData?.eventName || primaryRequest?.exhibition_name || primaryRequest?.relExhibitionEvent?.title,
    event_id: aiFallbackData?.eventId || primaryRequest?.ee_id || primaryRequest?.relExhibitionEvent?.id,
    company_name: aiFallbackData?.companyName || primaryRequest?.company_name || primaryRequest?.relStand?.company_name,
    stand_number: aiFallbackData?.standNumber || primaryRequest?.stand_number || primaryRequest?.relStand?.stand_number,
    stand_id: aiFallbackData?.standId || primaryRequest?.stand_id || primaryRequest?.relStand?.id,
  });
  const primaryContextGroup = getPrimaryContextGroup(contextGroups);
  const standNumbers = getNormalizedStandNumbers(
    [
      ...contextGroups.map((group) => group.stand_number),
      ...requestGroups.map((group) => group.stand_number),
      ...(aiFallbackData?.standNumbers || []),
    ]
  );
  const eventName = pickFirstNonEmpty(
    primaryContextGroup?.event_name,
    primaryRequest?.exhibition_name,
    primaryRequest?.relExhibitionEvent?.title,
    aiFallbackData?.eventName
  );
  const companyName = pickFirstNonEmpty(
    primaryContextGroup?.company_name,
    primaryRequest?.company_name,
    primaryRequest?.relStand?.company_name,
    aiFallbackData?.companyName
  );
  const standNumber = pickFirstNonEmpty(
    primaryContextGroup?.stand_number,
    primaryRequest?.stand_number,
    primaryRequest?.relStand?.stand_number,
    aiFallbackData?.standNumber
  );
  const derivedQuestions = requests.length > 0
    ? requests
      .filter((request) => request.action === "QUESTION")
      .map((request) => ({
        id: pickFirstNonEmpty(request.id, `question-${incoming.id}`),
        text: pickFirstNonEmpty(request.item_notes, request.item_name, request.notes),
        status: mapQuestionStatus(request.status),
      }))
      .filter((question) => question.text)
    : aiFallbackData?.questions || [];
  const questions = appendAiQuestionFallback(
    derivedQuestions,
    aiFallbackData?.aiQuestionText || derivedAiMetadata.aiQuestionText,
    pickFirstNonEmpty(incoming.id)
  );
  const equipmentChanges = requests.length > 0
    ? requests
      .filter((request) => request.action && request.action !== "QUESTION")
      .map((request): TCommunicationEquipmentChange => {
        const suggestedEquipmentGroupId = pickFirstNonEmpty(request.relEquipment?.group_id) || undefined;

        return {
          id: pickFirstNonEmpty(request.id, `change-${incoming.id}`),
          action: toLowercaseAction(request.action),
          item_label: pickFirstNonEmpty(request.item_name, request.relEquipment?.title),
          event_name: pickFirstNonEmpty(request.exhibition_name, request.relExhibitionEvent?.title) || undefined,
          company_name: pickFirstNonEmpty(request.company_name, request.relStand?.company_name) || undefined,
          stand_number: pickFirstNonEmpty(request.stand_number, request.relStand?.stand_number) || undefined,
          equipment_title: pickFirstNonEmpty(request.relEquipment?.title) || undefined,
          notes: pickFirstNonEmpty(request.item_notes, request.notes) || undefined,
          qty: request.item_qty || 1,
          color: pickFirstNonEmpty(request.relEquipment?.color, request.relFlooring?.color) || null,
          equipment_id: pickFirstNonEmpty(request.equipment_id, request.relEquipment?.id) || undefined,
          equipment_group_id: suggestedEquipmentGroupId,
          suggested_equipment_group_id: suggestedEquipmentGroupId,
          suggestion_status: suggestedEquipmentGroupId ? "pending" : undefined,
        };
      })
      .filter((change) => change.item_label)
    : aiFallbackData?.finalChanges.equipment_changes || [];
  const standUpdates: TCommunicationStandUpdates = {
    flooring_id: pickFirstNonEmpty(
      flooringRequest?.flooring_id,
      flooringRequest?.relFlooring?.id
    ) || undefined,
    flooring_color: pickFirstNonEmpty(flooringRequest?.relFlooring?.color) || undefined,
  };
  const managerNote = uniqueValues(
    requests.map((request) => getString(request.notes)).filter(Boolean)
  ).join("\n\n");
  const finalChanges = createFinalChanges(equipmentChanges, standUpdates, managerNote);
  const hasRequiredMatch = areContextGroupsComplete(contextGroups);
  const hasMeaningfulRequestContent = Boolean(
    requestItems.length
    || questions.length
    || equipmentChanges.length
  );
  const isFullyApproved = requestItems.length > 0
    && requestItems.every((request) => request.status === "approved");
  const parserProcessedAt =
    getLatestDate([
      incoming.parsed_at,
      aiFallbackData?.parserProcessedAt,
      ...aiResponses.map((response) => pickFirstNonEmpty(response.updated_at, response.created_at)),
      ...requests.map((request) => pickFirstNonEmpty(request.processed_at)),
    ]);
  const senderEmail = pickFirstNonEmpty(
    extractEmailAddress(headerSenderValue),
    incoming.from_email
  );
  const senderName = pickFirstNonEmpty(
    extractSenderName(headerSenderValue),
    incoming.from_name
  );
  const senderFallback = pickFirstNonEmpty(headerSenderValue, senderEmail, senderName);

  return {
    id: pickFirstNonEmpty(incoming.id),
    source_email_id: pickFirstNonEmpty(incoming.id),
    request_source: aiFallbackData ? "ai_fallback" : "email_requests",
    ai_selected_response_id: aiFallbackData?.aiSelectedResponseId || derivedAiMetadata.aiSelectedResponseId || undefined,
    ai_provider: aiFallbackData?.aiProvider || derivedAiMetadata.aiProvider || undefined,
    ai_model: aiFallbackData?.aiModel || derivedAiMetadata.aiModel || undefined,
    ai_attempt:
      aiFallbackData?.aiAttempt
      ?? derivedAiMetadata.aiAttempt
      ?? undefined,
    ai_response_status:
      aiFallbackData?.aiResponseStatus
      ?? derivedAiMetadata.aiResponseStatus
      ?? undefined,
    ai_needs_review:
      aiFallbackData?.aiNeedsReview
      ?? derivedAiMetadata.aiNeedsReview
      ?? null,
    ai_response_confidence:
      aiFallbackData?.aiResponseConfidence
      ?? derivedAiMetadata.aiResponseConfidence
      ?? null,
    ai_response_time_ms:
      aiFallbackData?.aiResponseTimeMs
      ?? derivedAiMetadata.aiResponseTimeMs
      ?? null,
    ai_response_count_total:
      aiFallbackData?.aiResponseCountTotal
      ?? derivedAiMetadata.aiResponseCountTotal
      ?? null,
    ai_response_count_processing:
      aiFallbackData?.aiResponseCountProcessing
      ?? derivedAiMetadata.aiResponseCountProcessing
      ?? null,
    ai_has_parsed_response:
      aiFallbackData?.aiHasParsedResponse
      ?? derivedAiMetadata.aiHasParsedResponse,
    ai_has_data_response:
      aiFallbackData?.aiHasDataResponse
      ?? derivedAiMetadata.aiHasDataResponse,
    ai_question_text:
      aiFallbackData?.aiQuestionText
      || derivedAiMetadata.aiQuestionText
      || undefined,
    sender: senderName && senderEmail
      ? `${senderName} <${senderEmail}>`
      : senderFallback,
    sender_email: senderEmail || undefined,
    intent: pickFirstNonEmpty(aiFallbackData?.sourceIntent, incoming.intent) || undefined,
    subject: pickFirstNonEmpty(incoming.subject, originalEmailMeta?.subject),
    event_name: eventName,
    event_id: pickFirstNonEmpty(primaryRequest?.ee_id, aiFallbackData?.eventId) || undefined,
    stand_number: standNumber,
    stand_numbers: standNumbers.length ? standNumbers : getNormalizedStandNumbers([ standNumber ]),
    stand_id: pickFirstNonEmpty(primaryRequest?.stand_id, aiFallbackData?.standId) || undefined,
    cad_id:
      pickFirstNonEmpty(
        primaryContextGroup?.cad_id,
        primaryRequest?.relStand?.relCad?.id
      ) || undefined,
    stand_drawing_preview_url:
      pickFirstNonEmpty(
        primaryContextGroup?.stand_drawing_preview_url,
        primaryRequest?.relStand?.relCad?.getFilePathPreview
      ) || undefined,
    stand_drawing_file_path:
      pickFirstNonEmpty(
        primaryContextGroup?.stand_drawing_file_path,
        primaryRequest?.relStand?.relCad?.file_path
      ) || undefined,
    company_name: companyName,
    status:
      isFullyApproved
        ? CommunicationConst.statuses.APPROVED
        : hasRequiredMatch && hasMeaningfulRequestContent
          ? CommunicationConst.statuses.PENDING
          : CommunicationConst.statuses.UNIDENTIFIED,
    confidence: Math.max(
      aiFallbackData?.confidence || 0,
      ...requests.map((request) => normalizeConfidence(request.confidence))
    ),
    original_body: pickFirstNonEmpty(incoming.body_clean),
    original_email_meta: originalEmailMeta,
    ai_summary: extractAiSummary(incoming, requests, aiResponses, selectedAiPayload),
    ai_reason: extractAiReason(incoming, requests, aiResponses, selectedAiPayload),
    questions,
    request_items: requestItems,
    request_groups: requestGroups,
    context_groups: contextGroups,
    work_state: buildWorkState(requests, incoming),
    capabilities: buildTaskCapabilities(),
    organizer_id: pickFirstNonEmpty(primaryRequest?.relExhibitionEvent?.organizer_id) || undefined,
    exhibition_type_id:
      pickFirstNonEmpty(primaryRequest?.relExhibitionEvent?.exhibition_type_id) || undefined,
    extracted_changes: finalChanges,
    final_changes: finalChanges,
    received_at: pickFirstNonEmpty(incoming.received_at, incoming.created_at),
    parser_processed_at: parserProcessedAt,
    approved_at: undefined,
    approved_by: undefined,
    updated_at:
      getLatestDate([
        incoming.updated_at,
        incoming.parsed_at,
        incoming.received_at,
        ...aiResponses.map((response) => pickFirstNonEmpty(response.updated_at, response.created_at)),
        ...requests.map((request) =>
          pickFirstNonEmpty(request.processed_at, request.updated_at, request.created_at)
        ),
      ]) || pickFirstNonEmpty(incoming.received_at),
    demo_badge_key: undefined,
    demo_note_key: undefined,
  };
};

const mapTasksToAuditEntries = (tasks: TCommunicationTask[]): TCommunicationAuditEntry[] =>
  tasks
    .filter((task) =>
      task.status === CommunicationConst.statuses.APPROVED
      || task.status === CommunicationConst.statuses.DISMISSED
    )
    .map((task) => ({
      id: `audit-${task.id}`,
      task_id: task.id,
      source_email_id: task.source_email_id,
      outcome:
        task.status === CommunicationConst.statuses.DISMISSED
          ? "dismissed"
          : "approved",
      before_changes: task.extracted_changes,
      after_changes: task.final_changes,
      before_questions: task.questions,
      after_questions: task.questions,
      processed_at:
        task.dismissed_at
        || task.approved_at
        || task.updated_at
        || task.received_at,
      processed_by: task.dismissed_by || task.approved_by || "",
      dismissed_reason: task.dismissed_reason || undefined,
      excluded_context_groups: getExcludedContextGroups(task),
      email_snapshot: {
        sender: task.sender,
        subject: task.subject,
        original_body: task.original_body,
        event_name: task.event_name,
        company_name: task.company_name,
        stand_number: task.stand_number,
      },
    }));

const sortTasksForBoard = (
  left: TCommunicationTask,
  right: TCommunicationTask
): number => {
  const leftIsDemo = Boolean(left.demo_badge_key);
  const rightIsDemo = Boolean(right.demo_badge_key);

  if (leftIsDemo !== rightIsDemo) {
    return leftIsDemo ? -1 : 1;
  }

  return new Date(right.received_at).getTime() - new Date(left.received_at).getTime();
};

const compareStrings = (left: string | null | undefined, right: string | null | undefined): number =>
  (left || "").localeCompare(right || "");

const compareDates = (left: string | null | undefined, right: string | null | undefined): number =>
  new Date(left || 0).getTime() - new Date(right || 0).getTime();

const sortCommunicationTasks = (
  tasks: TCommunicationTask[],
  sortState?: TCommunicationSortState
): TCommunicationTask[] => {
  if (!sortState?.key || !sortState.order) {
    return [ ...tasks ];
  }

  const sortedTasks = [ ...tasks ];
  const direction = sortState.order === "ascend" ? 1 : -1;

  sortedTasks.sort((left, right) => {
    const leftIsDemo = Boolean(left.demo_badge_key);
    const rightIsDemo = Boolean(right.demo_badge_key);

    if (leftIsDemo !== rightIsDemo) {
      return leftIsDemo ? -1 : 1;
    }

    let compareResult = 0;

    switch (sortState.key) {
      case "sender":
        compareResult = compareStrings(left.sender, right.sender);
        break;
      case "event_name":
        compareResult = compareStrings(
          `${left.event_name} ${left.company_name}`,
          `${right.event_name} ${right.company_name}`
        );
        break;
      case "stand_number":
        compareResult = compareStrings(left.stand_number, right.stand_number);
        break;
      case "questions":
        compareResult = left.questions.length - right.questions.length;
        break;
      case "status":
        compareResult = compareStrings(left.status, right.status);
        break;
      case "received_at":
        compareResult = compareDates(left.received_at, right.received_at);
        break;
      case "processed_by":
        compareResult = compareStrings(
          left.dismissed_by || left.approved_by,
          right.dismissed_by || right.approved_by
        );
        break;
      case "processed_at":
        compareResult = compareDates(
          left.dismissed_at || left.approved_at,
          right.dismissed_at || right.approved_at
        );
        break;
      default:
        compareResult = 0;
        break;
    }

    return compareResult * direction;
  });

  return sortedTasks;
};

export const getCommunicationServerOrderBy = (
  sortState?: TCommunicationSortState
): TCommunicationOrderByClause[] => {
  const order = sortState?.order === "ascend" ? "ASC" : "DESC";

  switch (sortState?.key) {
    case "sender":
      return [ { column: "FROM_NAME", order } ];
    case "received_at":
    case undefined:
      return [ { column: "RECEIVED_AT", order } ];
    default:
      return [ { column: "RECEIVED_AT", order: "DESC" } ];
  }
};

const hasServerSortableKey = (sortState?: TCommunicationSortState): boolean =>
  !sortState?.key || sortState.key === "received_at" || sortState.key === "sender";

const buildFullTaskCacheKey = (filters: TCommunicationFilters): string =>
  JSON.stringify({
    event_name: filters.event_name.trim(),
    sender: filters.sender.trim(),
    company_name: filters.company_name.trim(),
    stand_number: filters.stand_number.trim(),
    received_date: filters.received_date || "",
  });

const getCachedFullTaskSet = (
  cache: Map<string, TCommunicationFullTaskCache>,
  key: string
): TCommunicationFullTaskCache | null => {
  const cachedValue = cache.get(key);

  if (!cachedValue) {
    return null;
  }

  // Refresh insertion order so the least-recently-used entry is evicted first.
  cache.delete(key);
  cache.set(key, cachedValue);
  return cachedValue;
};

const setCachedFullTaskSet = (
  cache: Map<string, TCommunicationFullTaskCache>,
  key: string,
  tasks: TCommunicationTask[],
  queueTotals: TCommunicationQueueTotals
): TCommunicationFullTaskCache => {
  const nextCacheEntry: TCommunicationFullTaskCache = {
    key,
    tasks,
    queueTotals,
  };

  if (cache.has(key)) {
    cache.delete(key);
  }

  cache.set(key, nextCacheEntry);

  while (cache.size > FULL_TASK_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value as string | undefined;

    if (!oldestKey) {
      break;
    }

    cache.delete(oldestKey);
  }

  return nextCacheEntry;
};

export const shouldLoadFullCommunicationTaskSet = (
  filters: TCommunicationFilters,
  currentTab: TCommunicationTab,
  sortState: TCommunicationSortState
): boolean =>
  currentTab !== "inbox"
  || hasRequestLevelFilters(filters)
  || !hasServerSortableKey(sortState);

const filterTasksByTab = (
  tasks: TCommunicationTask[],
  tab: TCommunicationTab
): TCommunicationTask[] => {
  switch (tab) {
    case "pending":
      return tasks.filter((task) => task.status === CommunicationConst.statuses.PENDING);
    case "unidentified":
      return tasks.filter((task) => task.status === CommunicationConst.statuses.UNIDENTIFIED);
    case "audit":
      return tasks.filter(
        (task) =>
          task.status === CommunicationConst.statuses.APPROVED
          || task.status === CommunicationConst.statuses.DISMISSED
      );
    case "inbox":
    default:
      return tasks.filter(
        (task) =>
          task.status !== CommunicationConst.statuses.APPROVED
          && task.status !== CommunicationConst.statuses.DISMISSED
      );
  }
};

const isTaskInTab = (task: TCommunicationTask, tab: TCommunicationTab): boolean =>
  filterTasksByTab([ task ], tab).length > 0;

const isTaskCountedToday = (value?: string | null): boolean =>
  Boolean(value) && getCommunicationDayKey(value) === getCommunicationDayKey();

const updateQueueTotalsForTaskChange = (
  currentTotals: TCommunicationQueueTotals,
  previousTask: TCommunicationTask,
  nextTask: TCommunicationTask
): TCommunicationQueueTotals => ({
  inbox:
    currentTotals.inbox
    + (isTaskInTab(nextTask, "inbox") ? 1 : 0)
    - (isTaskInTab(previousTask, "inbox") ? 1 : 0),
  pending:
    currentTotals.pending
    + (isTaskInTab(nextTask, "pending") ? 1 : 0)
    - (isTaskInTab(previousTask, "pending") ? 1 : 0),
  unidentified:
    currentTotals.unidentified
    + (isTaskInTab(nextTask, "unidentified") ? 1 : 0)
    - (isTaskInTab(previousTask, "unidentified") ? 1 : 0),
  parserProcessedToday:
    currentTotals.parserProcessedToday
    + (isTaskCountedToday(nextTask.parser_processed_at) ? 1 : 0)
    - (isTaskCountedToday(previousTask.parser_processed_at) ? 1 : 0),
  managerApprovedToday:
    currentTotals.managerApprovedToday
    + (isTaskCountedToday(nextTask.approved_at) ? 1 : 0)
    - (isTaskCountedToday(previousTask.approved_at) ? 1 : 0),
});

const updateServerTotalForTaskChange = (
  currentTotal: number,
  previousTask: TCommunicationTask,
  nextTask: TCommunicationTask,
  currentTab: TCommunicationTab
): number =>
  currentTotal
  + (isTaskInTab(nextTask, currentTab) ? 1 : 0)
  - (isTaskInTab(previousTask, currentTab) ? 1 : 0);

const buildBoardTasks = (
  incomings: TRawEmailIncoming[],
  filters: TCommunicationFilters,
  options?: { includeDemoTask?: boolean; preserveIncomingOrder?: boolean }
): TCommunicationTask[] => {
  const mappedTasks = Array.from(
    incomings
      .reduce<Map<string, TCommunicationTask>>((acc, incoming) => {
        const incomingId = getString(incoming.id);

        if (!incomingId) {
          return acc;
        }

        acc.set(incomingId, mapIncomingToTask(incoming));
        return acc;
      }, new Map<string, TCommunicationTask>())
      .values()
  )
    .filter((task) => matchesClientSideFilters(task, filters));

  const loadedTasks = options?.preserveIncomingOrder
    ? mappedTasks
    : [ ...mappedTasks ].sort(sortTasksForBoard);

  if (!options?.includeDemoTask) {
    return loadedTasks;
  }

  const demoTask = createDemoMultiStandTask();

  return [ demoTask, ...loadedTasks.filter((task) => task.id !== demoTask.id) ];
};

export const getCommunicationSummary = (tasks: TCommunicationTask[]): TCommunicationSummary => {
  const todayKey = getCommunicationDayKey();
  const queueTotals: TCommunicationQueueTotals = {
    inbox: tasks.filter((task) =>
      task.status !== CommunicationConst.statuses.APPROVED
      && task.status !== CommunicationConst.statuses.DISMISSED
    ).length,
    pending: tasks.filter((task) => task.status === CommunicationConst.statuses.PENDING).length,
    unidentified: tasks.filter((task) => task.status === CommunicationConst.statuses.UNIDENTIFIED)
      .length,
    parserProcessedToday: tasks.filter(
      (task) =>
        Boolean(task.parser_processed_at)
        && getCommunicationDayKey(task.parser_processed_at) === todayKey
    ).length,
    managerApprovedToday: tasks.filter(
      (task) =>
        Boolean(task.approved_at)
        && getCommunicationDayKey(task.approved_at) === todayKey
    ).length,
  };

  return { queueTotals };
};

const createEmptyQueueTotals = (): TCommunicationQueueTotals => ({
  inbox: 0,
  pending: 0,
  unidentified: 0,
  parserProcessedToday: 0,
  managerApprovedToday: 0,
});

export const buildCommunicationTaskDigestSignature = (tasks: TCommunicationTask[]): string =>
  tasks
    .filter((task) => task.id !== DEMO_MULTI_STAND_TASK_ID)
    .map((task) => [
      task.id,
      task.received_at || "",
      task.updated_at || "",
    ].join(":"))
    .join("|");

const fetchPaginatedData = async <TItem,>(
  client: ApolloClient<object>,
  query: any,
  model: string,
  pageSize: number,
  baseVariables: Record<string, unknown>,
  isStale: () => boolean
): Promise<TItem[] | undefined> => {
  const aggregatedItems: TItem[] = [];
  let currentPage = 1;
  let lastPage = 1;

  do {
    if (isStale()) {
      return undefined;
    }

    const response = await client.query<{ [ key: string ]: TPaginatorData<TItem> }>({
      query,
      variables: {
        ...baseVariables,
        first: pageSize,
        page: currentPage,
      },
      fetchPolicy: "network-only",
    });

    const payload = response.data?.[ model ];
    aggregatedItems.push(...(payload?.data || []));
    lastPage = payload?.paginatorInfo?.lastPage || currentPage;
    currentPage += 1;
  } while (currentPage <= lastPage);

  return aggregatedItems;
};

const fetchFilteredIncomings = async (
  client: ApolloClient<object>,
  filters: TCommunicationFilters,
  orderBy: TCommunicationOrderByClause[],
  isStale: () => boolean
): Promise<TRawEmailIncoming[] | undefined> => {
  const incomingWhere = buildCommunicationIncomingWhere(filters);

  return fetchPaginatedData<TRawEmailIncoming>(
    client,
    GET_EMAIL_INCOMINGS,
    "emailIncomings",
    INCOMING_PAGE_SIZE,
    {
      where: incomingWhere,
      orderBy,
    },
    isStale
  );
};

export const useCommunicationStore = (
  filters: TCommunicationFilters,
  options: TCommunicationStoreOptions,
  currentTab: TCommunicationTab,
  sortState: TCommunicationSortState,
  currentManagerName?: string
) => {
  const client = useApolloClient();
  const requestIdRef = useRef(0);
  const totalsRequestIdRef = useRef(0);
  const fullTaskCacheRef = useRef<Map<string, TCommunicationFullTaskCache>>(new Map());
  const [ tasks, setTasks ] = useState<TCommunicationTask[]>([]);
  const [ overviewTasks, setOverviewTasks ] = useState<TCommunicationTask[]>([]);
  const [ queueTotals, setQueueTotals ] = useState<TCommunicationQueueTotals>(createEmptyQueueTotals);
  const [ queueTotalsLoading, setQueueTotalsLoading ] = useState<boolean>(true);
  const [ serverTotal, setServerTotal ] = useState<number>(0);
  const [ loading, setLoading ] = useState<boolean>(true);
  const [ error, setError ] = useState<string>();
  const needsFullTaskSet = shouldLoadFullCommunicationTaskSet(filters, currentTab, sortState);
  const fullTaskCacheKey = buildFullTaskCacheKey(filters);
  const taskDigestSignature = useMemo(() => buildCommunicationTaskDigestSignature(tasks), [ tasks ]);

  const loadQueueTotals = useCallback(async (mode: "foreground" | "background" = "foreground") => {
    if (needsFullTaskSet) {
      return;
    }

    const cachedFullTaskSet =
      mode === "background"
        ? null
        : getCachedFullTaskSet(fullTaskCacheRef.current, fullTaskCacheKey);

    if (cachedFullTaskSet) {
      setQueueTotals(cachedFullTaskSet.queueTotals);
      setOverviewTasks(cachedFullTaskSet.tasks);
      setQueueTotalsLoading(false);
      return;
    }

    const currentRequestId = ++totalsRequestIdRef.current;
    const isStale = () => currentRequestId !== totalsRequestIdRef.current;

    if (mode === "foreground") {
      setQueueTotalsLoading(true);
    }

    try {
      const incomings = await fetchFilteredIncomings(
        client,
        filters,
        [ { column: "RECEIVED_AT", order: "DESC" } ],
        isStale
      );

      if (!incomings || isStale()) {
        return;
      }

      const allTasks = buildBoardTasks(incomings, filters, { includeDemoTask: true });
      const nextQueueTotals = getCommunicationSummary(allTasks).queueTotals;

      setCachedFullTaskSet(
        fullTaskCacheRef.current,
        fullTaskCacheKey,
        allTasks,
        nextQueueTotals
      );

      setQueueTotals(nextQueueTotals);
      setOverviewTasks(allTasks);
      setQueueTotalsLoading(false);
    } catch {
      if (isStale()) {
        return;
      }

      if (mode === "foreground") {
        setQueueTotals(createEmptyQueueTotals());
        setOverviewTasks([]);
        setQueueTotalsLoading(false);
      }
    }
  }, [
    client,
    filters.company_name,
    filters.event_name,
    filters.received_date,
    filters.sender,
    filters.stand_number,
    fullTaskCacheKey,
    needsFullTaskSet,
  ]);

  const loadTasks = useCallback(async (mode: "foreground" | "background" = "foreground") => {
    const currentRequestId = ++requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;
    const nextOrderBy = getCommunicationServerOrderBy(sortState);
    const cachedFullTaskSet =
      mode === "background"
        ? null
        : getCachedFullTaskSet(fullTaskCacheRef.current, fullTaskCacheKey);

    if (mode === "foreground") {
      setLoading(true);
    }
    setError(undefined);

    try {
      if (needsFullTaskSet) {
        if (cachedFullTaskSet) {
          const tabTasks = filterTasksByTab(
            sortCommunicationTasks(cachedFullTaskSet.tasks, sortState),
            currentTab
          );
          const nextServerTotal = tabTasks.length;
          const offset = Math.max(0, (options.page - 1) * options.pageSize);

          setQueueTotals(cachedFullTaskSet.queueTotals);
          setOverviewTasks(cachedFullTaskSet.tasks);
          setQueueTotalsLoading(false);
          setTasks(tabTasks.slice(offset, offset + options.pageSize));
          setServerTotal(nextServerTotal);
          if (mode === "foreground") {
            setLoading(false);
          }
          return;
        }

        if (mode === "foreground") {
          setQueueTotalsLoading(true);
        }

        const incomings = await fetchFilteredIncomings(
          client,
          filters,
          [ { column: "RECEIVED_AT", order: "DESC" } ],
          isStale
        );

        if (!incomings || isStale()) {
          return;
        }

        const allTasks = buildBoardTasks(incomings, filters, {
          includeDemoTask: true,
        });
        const tabTasks = filterTasksByTab(
          sortCommunicationTasks(allTasks, sortState),
          currentTab
        );
        const nextQueueTotals = getCommunicationSummary(allTasks).queueTotals;
        const nextServerTotal = tabTasks.length;
        const offset = Math.max(0, (options.page - 1) * options.pageSize);

        setCachedFullTaskSet(
          fullTaskCacheRef.current,
          fullTaskCacheKey,
          allTasks,
          nextQueueTotals
        );

        setQueueTotals(nextQueueTotals);
        setOverviewTasks(allTasks);
        setQueueTotalsLoading(false);
        setTasks(tabTasks.slice(offset, offset + options.pageSize));
        setServerTotal(nextServerTotal);
      } else {
        const incomingWhere = buildCommunicationIncomingWhere(filters);
        const incomingResponse = await client.query<{ emailIncomings: TPaginatorData<TRawEmailIncoming> }>({
          query: GET_EMAIL_INCOMINGS,
          variables: {
            first: options.pageSize,
            page: options.page,
            where: incomingWhere,
            orderBy: nextOrderBy,
          },
          fetchPolicy: "network-only",
        });

        if (isStale()) {
          return;
        }

        const nextTasks = buildBoardTasks(
          incomingResponse.data?.emailIncomings?.data || [],
          filters,
          {
            includeDemoTask: options.page === 1,
            preserveIncomingOrder: true,
          }
        );

        setTasks(nextTasks);
        setServerTotal(incomingResponse.data?.emailIncomings?.paginatorInfo?.total || 0);
      }
      if (mode === "foreground") {
        setLoading(false);
      }
    } catch (nextError) {
      if (isStale()) {
        return;
      }

      setError(nextError instanceof Error ? nextError.message : "Failed to load communications");

      if (mode === "foreground") {
        setTasks([]);
        setOverviewTasks([]);
        setQueueTotalsLoading(false);
        setLoading(false);
      }
    }
  }, [
    client,
    currentTab,
    options.page,
    options.pageSize,
    filters.company_name,
    filters.event_name,
    filters.received_date,
    filters.sender,
    filters.stand_number,
    fullTaskCacheKey,
    needsFullTaskSet,
    sortState.key,
    sortState.order,
  ]);

  const refreshNow = useCallback(async () => {
    await Promise.all([
      loadTasks("background"),
      loadQueueTotals("background"),
    ]);
  }, [ loadQueueTotals, loadTasks ]);

  useEffect(() => {
    loadTasks("foreground");

    return () => {
      requestIdRef.current += 1;
    };
  }, [ loadTasks ]);

  useEffect(() => {
    loadQueueTotals("foreground");

    return () => {
      totalsRequestIdRef.current += 1;
    };
  }, [ loadQueueTotals ]);

  const updateFullTaskCache = useCallback((
    previousTask: TCommunicationTask,
    nextTask: TCommunicationTask
  ) => {
    const cachedFullTaskSet = getCachedFullTaskSet(fullTaskCacheRef.current, fullTaskCacheKey);

    if (!cachedFullTaskSet) {
      return;
    }

    cachedFullTaskSet.tasks = cachedFullTaskSet.tasks.map((task) =>
      task.id === previousTask.id ? nextTask : task
    );
    cachedFullTaskSet.queueTotals = updateQueueTotalsForTaskChange(
      cachedFullTaskSet.queueTotals,
      previousTask,
      nextTask
    );
    setOverviewTasks([ ...cachedFullTaskSet.tasks ]);
  }, [ fullTaskCacheKey ]);

  const auditEntries = useMemo(() => mapTasksToAuditEntries(tasks), [ tasks ]);
  const summary = useMemo(() => ({ queueTotals }), [ queueTotals ]);
  const saveTask = useCallback((taskId: string, values: TCommunicationTaskFormValues) => {
    const previousTask = tasks.find((task) => task.id === taskId);

    if (!previousTask) {
      return;
    }

    const nextTask = patchTaskWithFormValues(previousTask, values, {
      touchedAt: new Date().toISOString(),
      assignee: currentManagerName,
    });

    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId
          ? nextTask
          : task
      )
    );
    setQueueTotals((currentTotals) =>
      updateQueueTotalsForTaskChange(currentTotals, previousTask, nextTask)
    );
    setServerTotal((currentTotal) =>
      updateServerTotalForTaskChange(currentTotal, previousTask, nextTask, currentTab)
    );
    updateFullTaskCache(previousTask, nextTask);
  }, [ currentManagerName, currentTab, tasks, updateFullTaskCache ]);
  const approveTask = useCallback(
    (taskId: string, values: TCommunicationTaskFormValues) => {
      const previousTask = tasks.find((task) => task.id === taskId);

      if (!previousTask) {
        return;
      }

      const approvedAt = new Date().toISOString();
      const approvedBy = getString(currentManagerName) || "Current manager";
      const patchedTask = patchTaskWithFormValues(previousTask, values, {
        touchedAt: approvedAt,
        assignee: approvedBy,
        isApproved: true,
      });
      const nextTask = {
        ...patchedTask,
        status: getTaskStatusFromValues(values, true),
        approved_at: approvedAt,
        approved_by: approvedBy,
        updated_at: approvedAt,
        work_state: {
          ...patchedTask.work_state,
          status: "approved" as const,
          is_placeholder: true,
          assignee: approvedBy,
          touched_at: approvedAt,
        },
      };

      setTasks((currentTasks) =>
        currentTasks.map((task) => (task.id === taskId ? nextTask : task))
      );
      setQueueTotals((currentTotals) =>
        updateQueueTotalsForTaskChange(currentTotals, previousTask, nextTask)
      );
      setServerTotal((currentTotal) =>
        updateServerTotalForTaskChange(currentTotal, previousTask, nextTask, currentTab)
      );
      updateFullTaskCache(previousTask, nextTask);
    },
    [ currentManagerName, currentTab, tasks, updateFullTaskCache ]
  );

  const markAsInProgress = useCallback((taskId: string) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId && task.work_state.status === "untouched"
          ? {
            ...task,
            work_state: {
              ...task.work_state,
              status: "in_progress" as const,
              is_placeholder: true,
              assignee: currentManagerName,
              touched_at: new Date().toISOString(),
              viewed_at: task.work_state.viewed_at || new Date().toISOString(),
            },
          }
          : task
      )
    );
  }, [ currentManagerName ]);

  const markAsViewed = useCallback((taskId: string) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId && !task.work_state.viewed_at
          ? {
            ...task,
            work_state: {
              ...task.work_state,
              is_placeholder: true,
              viewed_at: new Date().toISOString(),
            },
          }
          : task
      )
    );
  }, []);

  const dismissTask = useCallback((taskId: string, reason: TCommunicationDismissReason) => {
    const previousTask = tasks.find((task) => task.id === taskId);

    if (!previousTask) {
      return;
    }

    const dismissedAt = new Date().toISOString();
    const dismissedBy = getString(currentManagerName) || "Current manager";
    const nextTask = {
      ...previousTask,
      status: CommunicationConst.statuses.DISMISSED,
      dismissed_at: dismissedAt,
      dismissed_by: dismissedBy,
      dismissed_reason: reason,
      updated_at: dismissedAt,
      work_state: {
        ...previousTask.work_state,
        status: "approved" as const,
        is_placeholder: true,
        assignee: dismissedBy,
        touched_at: dismissedAt,
        viewed_at: previousTask.work_state.viewed_at || dismissedAt,
      },
    };

    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        task.id === taskId ? nextTask : task
      )
    );
    setQueueTotals((currentTotals) =>
      updateQueueTotalsForTaskChange(currentTotals, previousTask, nextTask)
    );
    setServerTotal((currentTotal) =>
      updateServerTotalForTaskChange(currentTotal, previousTask, nextTask, currentTab)
    );
    updateFullTaskCache(previousTask, nextTask);
  }, [ currentManagerName, currentTab, tasks, updateFullTaskCache ]);

  const reopenTask = useCallback((taskId: string) => {
    const previousTask = tasks.find((task) => task.id === taskId);

    if (!previousTask || previousTask.status !== CommunicationConst.statuses.APPROVED) {
      return;
    }

    const reopenedAt = new Date().toISOString();
    const reopenedBy = getString(currentManagerName) || "Current manager";
    const nextStatus = getTaskStatusFromValues(previousTask);
    const nextTask = {
      ...previousTask,
      status: nextStatus,
      approved_at: undefined,
      approved_by: undefined,
      dismissed_at: undefined,
      dismissed_by: undefined,
      dismissed_reason: undefined,
      updated_at: reopenedAt,
      work_state: {
        ...previousTask.work_state,
        status: "in_progress" as const,
        is_placeholder: true,
        assignee: reopenedBy,
        touched_at: reopenedAt,
        viewed_at: previousTask.work_state.viewed_at || reopenedAt,
      },
    };

    setTasks((currentTasks) =>
      currentTasks.map((task) => (task.id === taskId ? nextTask : task))
    );
    setQueueTotals((currentTotals) =>
      updateQueueTotalsForTaskChange(currentTotals, previousTask, nextTask)
    );
    setServerTotal((currentTotal) =>
      updateServerTotalForTaskChange(currentTotal, previousTask, nextTask, currentTab)
    );
    updateFullTaskCache(previousTask, nextTask);
  }, [ currentManagerName, currentTab, tasks, updateFullTaskCache ]);

  return {
    tasks,
    overviewTasks,
    queueTotals,
    queueTotalsLoading,
    serverTotal,
    auditEntries,
    saveTask,
    approveTask,
    reopenTask,
    dismissTask,
    markAsViewed,
    markAsInProgress,
    summary,
    loading,
    error,
    refreshNow,
    taskDigestSignature,
  };
};
